import React, { useState, useEffect, useRef } from 'react';
import type { IntegrationSettings, DeliveryPlatform, ReceiptPrinter, BusinessInfo, SmartpayTerminal, IntegrationApproval, CurrentUser, BusinessHours, BusinessHoursDay, BusinessHoursPeriod, Weekday, MenuItem, EventStallMenuItem, EventStallOperator, EventProfile, Order } from '../types';
import { useMenuItems, useCategories, useCustomers } from '../hooks';
import MealPlansTab from '../components/loyalty/MealPlansTab';
import { getUberAuthorizationUrl, getCachedClientCredentialsToken, isUberOAuthConfigured, isUberCredentialsConfigured, isUberTokenProxyAvailable, testUberConnection, testUberConnectionViaCallable, syncUberEatsMenuViaCallable } from '../lib/uberEatsAuth';
import PlusIcon from '../components/icons/PlusIcon';
import EditIcon from '../components/icons/EditIcon';
import TrashIcon from '../components/icons/TrashIcon';
import PrinterModal from '../components/settings/PrinterModal';
import { getSmartConnectService, type TerminalStatus } from '../lib/smartconnect';
import { uploadAlertSound } from '../lib/alertSoundUpload';
import { uploadInvoiceLogo } from '../lib/invoiceLogoUpload';
import { DEFAULT_KITCHEN_ALERT_SOUND, resolveKitchenAlertSoundUrl } from '../lib/kitchenAlertSound';
import { hashEventOperatorPin, randomPinSalt } from '../lib/eventOperatorPin';
import { cloneEventMenuItems, sanitizeEventMenuItems, normalizeEventStallSection, collectEventTerminalIdsFromStall } from '../lib/normalizeEventStallSettings';
import { orderMatchesEventProfile } from '../lib/eventOrderScope';
import { getInvoiceLogoDisplayUrl } from '../lib/clientInvoiceTemplate';
import CateringAlertsFields from '../components/settings/CateringAlertsFields';
import {
  envelopeFromPeriods,
  MAX_BUSINESS_HOUR_PERIODS,
  normalizeBusinessHours,
  resolveDayPeriods,
  validateBusinessHours,
} from '../lib/businessHours';
import {
  DEFAULT_HOLIDAY_SURCHARGE_LABEL,
  normalizeHolidayDateKey,
  normalizeHolidaySurchargeConfig,
} from '../lib/holidaySurcharge';
import { onlineSettingsSummary } from '../lib/auditSummaries';
import { writeClientAudit } from '../lib/clientAudit';
import { settingsAuditSummary } from '../lib/moduleAudit';

// Override Reasons Configuration Component
const OverrideReasonsConfig: React.FC<{
  reasons: string[];
  onUpdate: (reasons: string[]) => void;
}> = ({ reasons, onUpdate }) => {
  const [newReason, setNewReason] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState('');

  const handleAdd = () => {
    if (newReason.trim()) {
      onUpdate([...reasons, newReason.trim()]);
      setNewReason('');
    }
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setEditingValue(reasons[index]);
  };

  const handleSaveEdit = () => {
    if (editingIndex !== null && editingValue.trim()) {
      const updated = [...reasons];
      updated[editingIndex] = editingValue.trim();
      onUpdate(updated);
      setEditingIndex(null);
      setEditingValue('');
    }
  };

  const handleDelete = (index: number) => {
    if (window.confirm('Are you sure you want to delete this override reason?')) {
      onUpdate(reasons.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <input
          type="text"
          value={newReason}
          onChange={(e) => setNewReason(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          placeholder="Enter new override reason..."
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
        />
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
        </button>
      </div>
      <div className="space-y-2">
        {reasons.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No override reasons configured. Add one above.</p>
        ) : (
          reasons.map((reason, index) => (
            <div key={index} className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-slate-200">
              {editingIndex === index ? (
                <>
                  <input
                    type="text"
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()}
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                  <button
                    onClick={handleSaveEdit}
                    className="px-2 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingIndex(null);
                      setEditingValue('');
                    }}
                    className="px-2 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm font-medium">{reason}</span>
                  <button
                    onClick={() => handleEdit(index)}
                    className="p-1 text-blue-600 hover:text-blue-800"
                  >
                    <EditIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(index)}
                    className="p-1 text-red-600 hover:text-red-800"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

interface SettingsPageProps {
  settings: IntegrationSettings;
  orders: Order[];
  onSave: (settings: IntegrationSettings) => void | Promise<void>;
  onUpdateTerminal: (terminalId: string, updates: Partial<SmartpayTerminal>) => void;
  onAddTerminal: (terminal: SmartpayTerminal) => void;
  onDeleteTerminal: (terminalId: string) => void;
  onSavePrinter: (printer: ReceiptPrinter) => void;
  onDeletePrinter: (printerId: string) => void;
  onClearMenuPhotos?: () => Promise<void>;
  currentUser?: CurrentUser;
}

// Integration Approval Badge Component
const IntegrationApprovalBadge: React.FC<{
  approval?: IntegrationApproval;
  integrationName: string;
  currentUser?: CurrentUser;
  onUpdateApproval: (status: IntegrationApproval['status'], notes?: string) => void;
}> = ({ approval, integrationName, currentUser, onUpdateApproval }) => {
  const canTest = currentUser?.permissions?.has('test_integrations');
  const canApprove = currentUser?.permissions?.has('approve_integrations');
  
  if (!canTest && !canApprove) {
    // Show read-only status if user doesn't have permissions
    if (!approval) return null;
    
    const statusColors = {
      pending: 'bg-gray-100 text-gray-800',
      testing: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    
    return (
      <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${statusColors[approval.status]}`}>
        {approval.status.charAt(0).toUpperCase() + approval.status.slice(1)}
      </div>
    );
  }
  
  const statusColors = {
    pending: 'bg-gray-100 text-gray-800',
    testing: 'bg-blue-100 text-blue-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
  };
  
  const [showModal, setShowModal] = useState(false);
  const [notes, setNotes] = useState(approval?.notes || '');
  const [selectedStatus, setSelectedStatus] = useState<IntegrationApproval['status']>(approval?.status || 'pending');
  
  const handleSave = () => {
    onUpdateApproval(selectedStatus, notes);
    setShowModal(false);
  };
  
  return (
    <>
      <div className="flex items-center gap-2">
        <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${statusColors[approval?.status || 'pending']}`}>
          {approval?.status ? approval.status.charAt(0).toUpperCase() + approval.status.slice(1) : 'Pending'}
        </div>
        {(canTest || canApprove) && (
          <button
            onClick={() => setShowModal(true)}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            {approval?.status === 'approved' ? 'Update' : 'Test & Approve'}
          </button>
        )}
      </div>
      
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold mb-4">Integration Approval - {integrationName}</h3>
            
            <div className="space-y-4">
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value as IntegrationApproval['status'])}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="pending">Pending</option>
                  <option value="testing">Testing</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about testing or approval..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/** Store object returned by Uber Eats GET /v1/eats/stores */
interface UberEatsStore {
  store_id?: string;
  name?: string;
  status?: string;
  [key: string]: unknown;
}

const DeliveryPlatformSettings: React.FC<{ 
    platform: DeliveryPlatform, 
    config: IntegrationSettings[DeliveryPlatform],
    onChange: (platform: DeliveryPlatform, key: string, value: any) => void;
    currentUser?: CurrentUser;
    onUpdateApproval?: (platform: DeliveryPlatform, status: IntegrationApproval['status'], notes?: string) => void;
}> = ({ platform, config, onChange, currentUser, onUpdateApproval }) => {
  const [testStatus, setTestStatus] = useState<{ loading?: boolean; message: string; ok?: boolean } | null>(null);
  const [lastTestStores, setLastTestStores] = useState<UberEatsStore[]>([]);
  const [syncMenuStatus, setSyncMenuStatus] = useState<{ loading?: boolean; message: string; ok?: boolean; categoriesCount?: number; itemsCount?: number } | null>(null);
  const isUber = platform === 'Uber Eats';
  const uberConfig = isUber ? config as IntegrationSettings['Uber Eats'] : null;
  const hasUberToken = !!uberConfig?.accessToken;
  const hasUberCredentialsInForm = isUber && !!(config.apiKey?.trim() && config.clientSecret);
  const canTestUber = hasUberToken || isUberCredentialsConfigured() || hasUberCredentialsInForm || (isUber && isUberTokenProxyAvailable());

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value, type, checked } = e.target;
      onChange(platform, name, type === 'checkbox' ? checked : value);
  };
  
  const handleApprovalUpdate = (status: IntegrationApproval['status'], notes?: string) => {
    if (onUpdateApproval) {
      onUpdateApproval(platform, status, notes);
    }
  };

  const handleConnectUber = () => {
    window.location.href = getUberAuthorizationUrl();
  };

  const handleTestConnection = async () => {
    if (!isUber) return;
    setTestStatus({ loading: true, message: 'Testing…' });
    try {
      // Use callable in browser to avoid CORS (Uber API does not allow cross-origin from localhost).
      const useCallable = typeof window !== 'undefined';
      let result: { stores?: unknown[]; next_key?: string };
      if (useCallable) {
        result = await testUberConnectionViaCallable();
      } else {
        let token: string;
        if (uberConfig?.accessToken) {
          token = uberConfig.accessToken;
        } else {
          const credentials = hasUberCredentialsInForm
            ? { clientId: config.apiKey?.trim(), clientSecret: config.clientSecret }
            : undefined;
          const tokenRes = await getCachedClientCredentialsToken(credentials);
          token = tokenRes.access_token;
        }
        result = await testUberConnection(token);
      }
      const count = result.stores?.length ?? 0;
      const stores = (result.stores ?? []) as UberEatsStore[];
      setLastTestStores(stores);
      setTestStatus({ ok: true, message: `Connected. Stores returned: ${count}.` });
    } catch (err) {
      setTestStatus({ ok: false, message: (err as Error).message });
      setLastTestStores([]);
    }
  };

  const handleSelectStore = (storeId: string) => {
    if (!storeId) return;
    onChange(platform, 'storeId', storeId);
  };

  const handleSyncMenu = async () => {
    if (!isUber || !config.storeId) return;
    setSyncMenuStatus({ loading: true, message: 'Syncing menu…' });
    try {
      const result = await syncUberEatsMenuViaCallable();
      setSyncMenuStatus({
        ok: true,
        message: `Menu synced: ${result.categoriesCount} categories, ${result.itemsCount} items.`,
        categoriesCount: result.categoriesCount,
        itemsCount: result.itemsCount,
      });
    } catch (err) {
      setSyncMenuStatus({ ok: false, message: (err as Error).message });
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-gray-800">{platform}</h2>
          {config.approval && (
            <IntegrationApprovalBadge
              approval={config.approval}
              integrationName={platform}
              currentUser={currentUser}
              onUpdateApproval={handleApprovalUpdate}
            />
          )}
        </div>
        <label htmlFor={`${platform}-enabled`} className="flex items-center cursor-pointer">
          <div className="relative">
            <input type="checkbox" id={`${platform}-enabled`} name="enabled" className="sr-only" checked={config.enabled} onChange={handleInputChange}/>
            <div className={`block w-14 h-8 rounded-full ${config.enabled ? 'bg-pink-500' : 'bg-gray-300'}`}></div>
            <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${config.enabled ? 'transform translate-x-6' : ''}`}></div>
          </div>
        </label>
      </div>
      <div className={`p-6 space-y-4 ${!config.enabled && 'opacity-50'}`}>
        {isUber && (
          <>
            {hasUberToken && (
              <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
                Connected to Uber Eats
                {uberConfig.tokenExpiresAt && (
                  <span className="block text-green-700 mt-0.5">
                    Token expires: {new Date(uberConfig.tokenExpiresAt).toLocaleString()}
                  </span>
                )}
              </div>
            )}
            {isUberOAuthConfigured() && !hasUberToken && (
              <div>
                <button type="button" onClick={handleConnectUber} disabled={!config.enabled} className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium">
                  Connect with Uber (OAuth)
                </button>
                <p className="text-xs text-gray-500 mt-1">Redirects to Uber to authorize; you will return here after approval.</p>
              </div>
            )}
          </>
        )}
        <div className="settings-field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{isUber ? 'Client ID' : 'API Key'}</label>
          <input type="text" name="apiKey" value={config.apiKey} onChange={handleInputChange} disabled={!config.enabled} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"/>
          {isUber && <p className="text-xs text-gray-500 mt-0.5">Set via VITE_UBER_CLIENT_ID or paste here for display.</p>}
        </div>
        <div className="settings-field">
          <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
          <input type="password" name="clientSecret" value={config.clientSecret} onChange={handleInputChange} disabled={!config.enabled} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"/>
          {isUber && <p className="text-xs text-gray-500 mt-0.5">For OAuth, set VITE_UBER_CLIENT_SECRET in environment (not stored in app).</p>}
        </div>
        <div className="settings-field">
          <label className="block text-sm font-medium text-gray-700 mb-1">Store ID</label>
          <input type="text" name="storeId" value={config.storeId ?? ''} onChange={handleInputChange} disabled={!config.enabled} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-pink-500 focus:border-pink-500"/>
          {isUber && <p className="text-xs text-gray-500 mt-0.5">Pick a store below after Test Connection, or enter a store ID manually.</p>}
        </div>
        <div className="pt-2 flex items-center justify-between gap-2 flex-wrap">
          {testStatus && (
            <span className={`text-sm ${testStatus.ok ? 'text-green-600' : 'text-red-600'}`}>
              {testStatus.loading ? testStatus.message : testStatus.message}
            </span>
          )}
          <button type="button" onClick={handleTestConnection} disabled={!config.enabled || (isUber && !canTestUber)} className="text-sm font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed">
            {testStatus?.loading ? 'Testing…' : 'Test Connection'}
          </button>
        </div>
        {isUber && lastTestStores.length > 0 && (
          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
            <p className="text-sm font-medium text-gray-700 mb-2">Stores from Uber Eats – select one to use for this POS:</p>
            <ul className="space-y-2">
              {lastTestStores.map((store) => {
                const raw = store as Record<string, unknown>;
                const id = (store.store_id ?? raw.store_id ?? raw.id ?? raw.storeId) as string | undefined;
                const name = (store.name ?? raw.name ?? 'Unnamed store') as string;
                if (!id) return null;
                const isSelected = config.storeId === id;
                return (
                  <li key={id} className={`flex items-center justify-between gap-2 py-1.5 px-2 rounded border ${isSelected ? 'bg-pink-50 border-pink-300' : 'bg-white border-gray-200'}`}>
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm text-gray-800 truncate">{name}</span>
                      <span className="text-[11px] text-gray-500 break-all">Store ID: {id}</span>
                    </div>
                    <div className="flex-shrink-0">
                      {isSelected ? (
                        <span className="text-xs font-medium text-pink-600">Selected</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleSelectStore(id)}
                          className="text-xs font-medium text-pink-600 hover:text-pink-800 whitespace-nowrap"
                        >
                          Use this store
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            <p className="text-xs text-gray-500 mt-2">Then click Save at the bottom of the page to persist.</p>
          </div>
        )}
        {isUber && config.storeId && (
          <div className="border-t border-gray-200 pt-4 mt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Menu sync</p>
            <p className="text-xs text-gray-500 mb-2">Push this POS menu (categories and items) to Uber Eats for the selected store.</p>
            {syncMenuStatus && (
              <p className={`text-sm mb-2 ${syncMenuStatus.ok ? 'text-green-600' : 'text-red-600'}`}>
                {syncMenuStatus.loading ? syncMenuStatus.message : syncMenuStatus.message}
              </p>
            )}
            <button
              type="button"
              onClick={handleSyncMenu}
              disabled={!config.enabled || !!syncMenuStatus?.loading}
              className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              {syncMenuStatus?.loading ? 'Syncing…' : 'Sync menu to Uber Eats'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

const SmartpaySettingsComponent: React.FC<{ 
  settings: IntegrationSettings;
  onUpdateTerminal: (terminalId: string, updates: Partial<SmartpayTerminal>) => void;
  onAddTerminal: (terminal: SmartpayTerminal) => void;
  onDeleteTerminal: (terminalId: string) => void;
  currentUser?: CurrentUser;
  onUpdateApproval?: (status: IntegrationApproval['status'], notes?: string) => void;
}> = ({ settings, onUpdateTerminal, onAddTerminal, onDeleteTerminal, currentUser, onUpdateApproval }) => {
    const [pairingCode, setPairingCode] = useState<{ [key: string]: string }>({});
    const [isLoading, setIsLoading] = useState<{ [key: string]: boolean }>({});
    const [terminalStatuses, setTerminalStatuses] = useState<{ [key: string]: TerminalStatus }>({});
    const [editingTerminal, setEditingTerminal] = useState<SmartpayTerminal | null>(null);
    const [isAddingTerminal, setIsAddingTerminal] = useState(false);
    const [newTerminalName, setNewTerminalName] = useState('');
    const [newRegisterName, setNewRegisterName] = useState('');

    const terminals = settings.smartpay?.terminals || [];
    const service = getSmartConnectService(
      settings.smartpay?.apiHost,
      settings.smartpay?.posVendorName,
      settings.smartpay?.posBusinessName
    );

    const generateUUID = () => {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    };

    const handlePair = async (terminal: SmartpayTerminal) => {
        const code = pairingCode[terminal.id]?.trim();
        if (!code) {
            alert("Please enter the pairing code from your terminal.");
            return;
        }
        
        setIsLoading({ ...isLoading, [terminal.id]: true });
        // Use the service instance that's already configured with the correct API host
        const result = await service.pairTerminal(code, terminal.registerId, terminal.registerName);
        
        if (result.success) {
            alert("Pairing successful!");
            onUpdateTerminal(terminal.id, { isPaired: true, lastPairedAt: Date.now() });
            setPairingCode({ ...pairingCode, [terminal.id]: '' });
        } else {
            alert(`Pairing failed: ${result.message}`);
            onUpdateTerminal(terminal.id, { isPaired: false });
        }
        setIsLoading({ ...isLoading, [terminal.id]: false });
    };

    const handleUnpair = (terminal: SmartpayTerminal) => {
        if (window.confirm("Are you sure you want to unpair? You will need to re-pair using a new code from your terminal.")) {
            onUpdateTerminal(terminal.id, { isPaired: false, lastPairedAt: undefined });
            alert("Terminal has been unpaired from this POS register.");
        }
    };

    const handleCheckStatus = async (terminal: SmartpayTerminal) => {
        setIsLoading({ ...isLoading, [terminal.id]: true });
        try {
            const status = await service.getTerminalStatus(terminal.registerId);
            setTerminalStatuses({ ...terminalStatuses, [terminal.id]: status });
        } catch (error: any) {
            alert(`Error checking status: ${error.message}`);
        }
        setIsLoading({ ...isLoading, [terminal.id]: false });
    };

    const handleAddTerminal = () => {
        if (!newTerminalName.trim() || !newRegisterName.trim()) {
            alert("Please enter both terminal name and register name.");
            return;
        }

        const newTerminal: SmartpayTerminal = {
            id: `terminal-${Date.now()}`,
            name: newTerminalName.trim(),
            registerId: generateUUID(),
            registerName: newRegisterName.trim(),
            isPaired: false,
        };

        onAddTerminal(newTerminal);
        setNewTerminalName('');
        setNewRegisterName('');
        setIsAddingTerminal(false);
    };

    const handleDeleteTerminal = (terminal: SmartpayTerminal) => {
        if (window.confirm(`Are you sure you want to delete "${terminal.name}"? This cannot be undone.`)) {
            onDeleteTerminal(terminal.id);
        }
    };

    const handleEditTerminal = (terminal: SmartpayTerminal) => {
        setEditingTerminal(terminal);
        setNewTerminalName(terminal.name);
        setNewRegisterName(terminal.registerName);
    };

    const handleSaveEdit = () => {
        if (!editingTerminal || !newTerminalName.trim() || !newRegisterName.trim()) {
            alert("Please enter both terminal name and register name.");
            return;
        }

        onUpdateTerminal(editingTerminal.id, {
            name: newTerminalName.trim(),
            registerName: newRegisterName.trim(),
        });

        setEditingTerminal(null);
        setNewTerminalName('');
        setNewRegisterName('');
    };
    
    const handleApprovalUpdate = (status: IntegrationApproval['status'], notes?: string) => {
      if (onUpdateApproval) {
        onUpdateApproval(status, notes);
      }
    };
    
    return (
        <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-bold text-gray-800">SmartPay Integration</h3>
                  {settings.smartpay?.approval && (
                    <IntegrationApprovalBadge
                      approval={settings.smartpay.approval}
                      integrationName="SmartPay"
                      currentUser={currentUser}
                      onUpdateApproval={handleApprovalUpdate}
                    />
                  )}
                </div>
                <button 
                    onClick={() => setIsAddingTerminal(true)} 
                    className="bg-blue-500 text-white font-semibold py-1 px-3 rounded-md text-sm hover:bg-blue-600 flex items-center"
                >
                    <PlusIcon className="w-4 h-4 mr-1"/> Add Terminal
                </button>
            </div>
            <div className="p-6 space-y-6">
                {/* Add/Edit Terminal Form */}
                {(isAddingTerminal || editingTerminal) && (
                    <div className="bg-gray-50 p-4 rounded-lg border-2 border-blue-200">
                        <h4 className="font-semibold mb-3">{editingTerminal ? 'Edit Terminal' : 'Add New Terminal'}</h4>
                        <div className="space-y-3">
                            <div className="settings-field">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Terminal Name</label>
                                <input
                                    type="text"
                                    value={newTerminalName}
                                    onChange={(e) => setNewTerminalName(e.target.value)}
                                    placeholder="e.g., Main Counter Terminal"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900"
                                />
                            </div>
                            <div className="settings-field">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Register Name</label>
                                <input
                                    type="text"
                                    value={newRegisterName}
                                    onChange={(e) => setNewRegisterName(e.target.value)}
                                    placeholder="e.g., Main Register"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900"
                                />
                            </div>
                            {editingTerminal && (
                                <div className="settings-field">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Register ID (GUID)</label>
                                    <div className="flex items-center">
                                        <input
                                            type="text"
                                            readOnly
                                            value={editingTerminal.registerId}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900"
                                        />
                                        <button
                                            onClick={() => navigator.clipboard.writeText(editingTerminal.registerId)}
                                            className="ml-2 p-2 bg-gray-200 rounded-md hover:bg-gray-300"
                                        >
                                            Copy
                                        </button>
                                    </div>
                                </div>
                            )}
                            <div className="flex gap-2">
                                <button
                                    onClick={editingTerminal ? handleSaveEdit : handleAddTerminal}
                                    className="bg-blue-500 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-600"
                                >
                                    {editingTerminal ? 'Save Changes' : 'Add Terminal'}
                                </button>
                                <button
                                    onClick={() => {
                                        setIsAddingTerminal(false);
                                        setEditingTerminal(null);
                                        setNewTerminalName('');
                                        setNewRegisterName('');
                                    }}
                                    className="bg-gray-200 text-gray-800 font-semibold py-2 px-4 rounded-md hover:bg-gray-300"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Terminals List */}
                {terminals.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No terminals configured. Click "Add Terminal" to get started.</p>
                ) : (
                    <div className="space-y-4">
                        {terminals.map((terminal) => {
                            const status = terminalStatuses[terminal.id];
                            return (
                                <div key={terminal.id} className="border rounded-lg p-4">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <h4 className="font-semibold text-gray-800">{terminal.name}</h4>
                                            <p className="text-sm text-gray-500">{terminal.registerName}</p>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleEditTerminal(terminal)}
                                                className="text-blue-600 hover:text-blue-900"
                                            >
                                                <EditIcon className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteTerminal(terminal)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                <TrashIcon className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Register ID</label>
                                            <div className="flex items-center">
                                                <input
                                                    type="text"
                                                    readOnly
                                                    value={terminal.registerId}
                                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-900 font-mono"
                                                />
                                                <button
                                                    onClick={() => navigator.clipboard.writeText(terminal.registerId)}
                                                    className="ml-2 p-1 bg-gray-200 rounded hover:bg-gray-300 text-xs"
                                                >
                                                    Copy
                                                </button>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <div>
                                                <span className="text-sm font-medium text-gray-700">Pairing Status: </span>
                                                <span className={`font-semibold ${terminal.isPaired ? 'text-green-600' : 'text-red-600'}`}>
                                                    {terminal.isPaired ? 'Paired' : 'Not Paired'}
                                                </span>
                                            </div>
                                            {status && (
                                                <div className="text-xs">
                                                    <span className={`font-semibold ${status.isReady ? 'text-green-600' : 'text-gray-500'}`}>
                                                        {status.isReady ? 'Ready' : 'Not Ready'}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {terminal.isPaired ? (
                                            <div className="space-y-2">
                                                <button
                                                    onClick={() => handleCheckStatus(terminal)}
                                                    disabled={isLoading[terminal.id]}
                                                    className="w-full bg-gray-200 text-gray-800 font-semibold py-2 px-4 rounded-md hover:bg-gray-300 disabled:bg-gray-400 text-sm"
                                                >
                                                    {isLoading[terminal.id] ? 'Checking...' : 'Check Status'}
                                                </button>
                                                <button
                                                    onClick={() => handleUnpair(terminal)}
                                                    className="w-full bg-red-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-red-600 text-sm"
                                                >
                                                    Unpair Terminal
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                <label className="block text-xs font-medium text-gray-700">Enter Pairing Code</label>
                                                <p className="text-xs text-gray-500 mb-1">Initiate pairing on your EFTPOS terminal to get a temporary code.</p>
                                                <div className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        value={pairingCode[terminal.id] || ''}
                                                        onChange={(e) => setPairingCode({ ...pairingCode, [terminal.id]: e.target.value })}
                                                        placeholder="Code from terminal"
                                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 placeholder-gray-500 text-sm"
                                                    />
                                                    <button
                                                        onClick={() => handlePair(terminal)}
                                                        disabled={isLoading[terminal.id]}
                                                        className="bg-blue-500 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-600 disabled:bg-gray-400 text-sm"
                                                    >
                                                        {isLoading[terminal.id] ? 'Pairing...' : 'Pair'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};


const weekDayOrder: Weekday[] = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];

const defaultBusinessHours: BusinessHours = {
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Pacific/Auckland',
  days: [
    { day: 'monday', open: '09:00', close: '22:00' },
    { day: 'tuesday', open: '09:00', close: '22:00' },
    { day: 'wednesday', open: '09:00', close: '22:00' },
    { day: 'thursday', open: '09:00', close: '23:00' },
    { day: 'friday', open: '09:00', close: '23:00' },
    { day: 'saturday', open: '09:00', close: '23:00' },
    { day: 'sunday', open: null, close: null, closed: true },
  ],
};

type EventMenuDraft = {
  name: string;
  price: string;
  unitCost: string;
  openingQuantity: string;
  categoryName: string;
  imageUrl: string;
  isAvailable: boolean;
};

const emptyEventMenuDraft: EventMenuDraft = {
  name: '',
  price: '',
  unitCost: '',
  openingQuantity: '',
  categoryName: '',
  imageUrl: '',
  isAvailable: true,
};

/** Merge saved category order with current menu category names (append missing, alpha). */
function mergeEventCategoryOrder(uniqueNames: string[], savedOrder: string[]): string[] {
  const set = new Set(uniqueNames.filter(Boolean));
  const out: string[] = [];
  for (const s of savedOrder) {
    const t = String(s ?? '').trim();
    if (t && set.has(t)) {
      out.push(t);
      set.delete(t);
    }
  }
  const rest = [...set].sort((a, b) => a.localeCompare(b));
  return [...out, ...rest];
}

const ensureSettingsDefaults = (settings: IntegrationSettings): IntegrationSettings => ({
  ...settings,
  kitchenSLA: {
    warningMinutes: settings.kitchenSLA?.warningMinutes ?? 5,
    dangerMinutes: settings.kitchenSLA?.dangerMinutes ?? 10,
    minutesToStart: settings.kitchenSLA?.minutesToStart ?? 5,
    minutesToServe: settings.kitchenSLA?.minutesToServe ?? 5,
    alertSoundEnabled: settings.kitchenSLA?.alertSoundEnabled ?? true,
    alertSoundUrl: resolveKitchenAlertSoundUrl(settings.kitchenSLA?.alertSoundUrl),
    alertSoundVolume: Math.min(100, Math.max(0, settings.kitchenSLA?.alertSoundVolume ?? 100)),
    kitchenDisplayTextSize: settings.kitchenSLA?.kitchenDisplayTextSize ?? 'large',
  },
  flashPnl: settings.flashPnl || {},
  businessInfo: settings.businessInfo || {
    businessName: 'KINGS PARK',
    address: '123 Restaurant Ave, Food City',
    gstNumber: '123-456-789',
    phone: '',
    email: '',
    website: '',
    emailFooterLine1: '',
    emailFooterLine2: '',
    googleReviewUrl: '',
  },
  buffetPricing: settings.buffetPricing || {
    breakfast: 0,
    lunch: 0,
    dinner: 0,
  },
  firstTable: settings.firstTable || {
    enabled: false,
    discountPercent: 50,
    eligibleCategoryIds: [],
    eligibleMenuItemIds: [],
  },
  eventStall: normalizeEventStallSection(settings),
  uberDoordashPriceIncreasePercent: settings.uberDoordashPriceIncreasePercent ?? 0,
  holidaySurcharge: normalizeHolidaySurchargeConfig(settings.holidaySurcharge) ?? {
    enabled: false,
    percent: 0,
    dates: [],
  },
  businessHours: settings.businessHours || defaultBusinessHours,
  recipeInventoryMode: settings.recipeInventoryMode ?? 'inventory_connected',
});

type SettingsSectionId =
  | 'hardware'
  | 'business'
  | 'online'
  | 'meal-plans'
  | 'catering'
  | 'menu-photos'
  | 'hours'
  | 'kitchen'
  | 'food-cost'
  | 'flash-pl'
  | 'overrides'
  | 'fire'
  | 'buffet'
  | 'first-table'
  | 'event'
  | 'delivery-manual'
  | 'platforms';

type SettingsNavItem = {
  id: SettingsSectionId;
  label: string;
  keywords: string;
  adminOnly?: boolean;
  requiresClearPhotos?: boolean;
};

const SETTINGS_NAV: { label: string; items: SettingsNavItem[] }[] = [
  {
    label: 'Store',
    items: [
      { id: 'business', label: 'Business Information', keywords: 'name address gst nzbn phone email website logo invoice review statement payroll' },
      { id: 'hours', label: 'Business Hours', keywords: 'open close timezone roster uber holiday surcharge public' },
      { id: 'menu-photos', label: 'Menu photos', keywords: 'images clear storage', requiresClearPhotos: true },
    ],
  },
  {
    label: 'Ordering',
    items: [
      { id: 'online', label: 'Online Ordering', keywords: 'qr domain link website' },
      { id: 'meal-plans', label: 'Meal plans', keywords: 'student weekly subscribers' },
      { id: 'catering', label: 'Catering request alerts', keywords: 'email quote loyalty' },
      { id: 'buffet', label: 'Buffet packages', keywords: 'breakfast lunch dinner' },
      { id: 'first-table', label: 'First Table Discount', keywords: 'percentage category menu' },
    ],
  },
  {
    label: 'Kitchen',
    items: [
      { id: 'kitchen', label: 'Kitchen SLA', keywords: 'display timer sound alert text size kds' },
    ],
  },
  {
    label: 'Payments',
    items: [
      { id: 'hardware', label: 'Hardware & Payments', keywords: 'smartpay terminal printer receipt eftpos' },
      { id: 'platforms', label: 'Delivery Platforms', keywords: 'uber eats doordash integration oauth' },
      { id: 'delivery-manual', label: 'Uber/DoorDash manual orders', keywords: 'price increase percent' },
    ],
  },
  {
    label: 'Events',
    items: [
      { id: 'event', label: 'Event / Stall (Mobile POS)', keywords: 'pin token menu operator profile' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { id: 'food-cost', label: 'Food cost & recipes', keywords: 'inventory recipe depletion' },
      { id: 'flash-pl', label: 'Flash P&L (Investor Format)', keywords: 'budget revenue cash investor', adminOnly: true },
      { id: 'overrides', label: 'Manager Override Reasons', keywords: 'comp discount reason' },
      { id: 'fire', label: 'Fire Evacuation Mode Settings', keywords: 'emergency cancel message' },
    ],
  },
];

function visibleSettingsNav(
  isAdmin: boolean,
  showMenuPhotos: boolean,
  query: string,
): { label: string; items: SettingsNavItem[] }[] {
  const q = query.trim().toLowerCase();
  return SETTINGS_NAV.map((group) => ({
    label: group.label,
    items: group.items.filter((item) => {
      if (item.adminOnly && !isAdmin) return false;
      if (item.requiresClearPhotos && !showMenuPhotos) return false;
      if (!q) return true;
      return `${item.label} ${item.keywords}`.toLowerCase().includes(q);
    }),
  })).filter((group) => group.items.length > 0);
}

const SettingsPage: React.FC<SettingsPageProps> = ({ settings, orders, onSave, onUpdateTerminal, onAddTerminal, onDeleteTerminal, onSavePrinter, onDeletePrinter, onClearMenuPhotos, currentUser }) => {
  const { menuItems } = useMenuItems();
  const { categories } = useCategories();
  const [currentSettings, setCurrentSettings] = useState(ensureSettingsDefaults(settings));
  const [saveNotice, setSaveNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeSection, setActiveSection] = useState<SettingsSectionId>('business');
  const [sectionQuery, setSectionQuery] = useState('');
  const settingsMainRef = useRef<HTMLElement>(null);
  const showMenuPhotos = Boolean(onClearMenuPhotos);
  const isAdmin = Boolean(currentUser?.isAdmin);
  const settingsNav = visibleSettingsNav(isAdmin, showMenuPhotos, sectionQuery);
  const isSection = (id: SettingsSectionId) => activeSection === id;
  const mealPlansOn = currentSettings.businessInfo?.mealPlansEnabled === true;
  const { customers } = useCustomers(mealPlansOn);
  const [isPrinterModalOpen, setIsPrinterModalOpen] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState<ReceiptPrinter | null>(null);
  const [isUploadingAlertSound, setIsUploadingAlertSound] = useState(false);
  const [isUploadingInvoiceLogo, setIsUploadingInvoiceLogo] = useState(false);
  const invoiceLogoFileInputRef = useRef<HTMLInputElement>(null);
  const [eventMenuDraft, setEventMenuDraft] = useState<EventMenuDraft>(emptyEventMenuDraft);
  const [editingEventMenuItemId, setEditingEventMenuItemId] = useState<number | null>(null);
  const [eventMenuError, setEventMenuError] = useState<string | null>(null);
  const [newEventOperatorName, setNewEventOperatorName] = useState('');
  const [newEventOperatorPin, setNewEventOperatorPin] = useState('');
  const [newEventName, setNewEventName] = useState('');
  const [eventTokenPadDraft, setEventTokenPadDraft] = useState(() => {
    const r = ensureSettingsDefaults(settings).eventStall?.eventTokenRollMax;
    return r != null && r >= 2 ? String(r) : '';
  });
  const [holidayDateDraft, setHolidayDateDraft] = useState('');
  const businessHours = currentSettings.businessHours || defaultBusinessHours;
  const holidaySurcharge =
    currentSettings.holidaySurcharge ?? { enabled: false, percent: 0, dates: [] as string[] };
  const activeEventId = currentSettings.eventStall?.activeEventId;
  const eventProfiles = currentSettings.eventStall?.events ?? [];
  const activeEventProfile = eventProfiles.find((evt) => evt.id === activeEventId) ?? null;
  const activeEventArchived = activeEventProfile?.status === 'archived';

  const applyActiveEventSnapshot = React.useCallback(
    (settings: IntegrationSettings, nextActiveEventId: string | undefined): IntegrationSettings => {
      const events = settings.eventStall?.events ?? [];
      const active = nextActiveEventId ? events.find((e) => e.id === nextActiveEventId) : undefined;
      return {
        ...settings,
        eventStall: {
          ...settings.eventStall,
          activeEventId: active?.id,
          name: active?.name ?? settings.eventStall?.name ?? '',
          eventMenuItems: active ? cloneEventMenuItems(active.menuSnapshot) : (settings.eventStall?.eventMenuItems ?? []),
          eventCategoryOrder: active?.eventCategoryOrderSnapshot ?? settings.eventStall?.eventCategoryOrder ?? [],
        },
      };
    },
    []
  );

  const syncActiveEventInState = React.useCallback(
    (updater: (active: EventProfile) => EventProfile) => {
      setCurrentSettings((prev) => {
        const events = prev.eventStall?.events ?? [];
        const activeId = prev.eventStall?.activeEventId;
        if (!activeId) return prev;
        const idx = events.findIndex((e) => e.id === activeId);
        if (idx < 0) return prev;
        const updatedActive = updater(events[idx]);
        const nextEvents = [...events];
        nextEvents[idx] = updatedActive;
        const nextSettings: IntegrationSettings = {
          ...prev,
          eventStall: {
            ...prev.eventStall,
            events: nextEvents,
          },
        };
        return applyActiveEventSnapshot(nextSettings, activeId);
      });
    },
    [applyActiveEventSnapshot]
  );

  const switchActiveEvent = (eventId: string) => {
    setCurrentSettings((prev) => applyActiveEventSnapshot(prev, eventId));
    setEditingEventMenuItemId(null);
    setEventMenuDraft(emptyEventMenuDraft);
    setEventMenuError(null);
  };

  const createEventProfile = (cloneFromActive: boolean) => {
    const name = newEventName.trim();
    if (!name) {
      alert('Event name is required.');
      return;
    }
    setCurrentSettings((prev) => {
      const events = prev.eventStall?.events ?? [];
      if (events.some((e) => e.name.toLowerCase() === name.toLowerCase())) {
        alert('An event with this name already exists.');
        return prev;
      }
      const now = Date.now();
      const sourceMenu = cloneFromActive
        ? cloneEventMenuItems(activeEventProfile?.menuSnapshot ?? prev.eventStall?.eventMenuItems ?? [])
        : [];
      const sourceOrder = cloneFromActive
        ? [...(activeEventProfile?.eventCategoryOrderSnapshot ?? prev.eventStall?.eventCategoryOrder ?? [])]
        : [];
      const newEvent: EventProfile = {
        id: `evt-${now}-${Math.random().toString(36).slice(2, 8)}`,
        name,
        status: 'active',
        createdAt: now,
        menuSnapshot: sourceMenu,
        eventCategoryOrderSnapshot: sourceOrder,
      };
      const nextSettings: IntegrationSettings = {
        ...prev,
        eventStall: {
          ...prev.eventStall,
          events: [...events, newEvent],
        },
      };
      return applyActiveEventSnapshot(nextSettings, newEvent.id);
    });
    setNewEventName('');
  };

  const archiveActiveEvent = () => {
    if (!activeEventProfile) return;
    if (!window.confirm(`Archive "${activeEventProfile.name}"? You can still view it in reports, but it won't be editable as active.`)) return;
    setCurrentSettings((prev) => {
      const events = prev.eventStall?.events ?? [];
      const idx = events.findIndex((e) => e.id === activeEventProfile.id);
      if (idx < 0) return prev;
      const nextEvents = [...events];
      nextEvents[idx] = { ...nextEvents[idx], status: 'archived', archivedAt: Date.now() };
      const nextActive = nextEvents.find((e) => e.status === 'active') ?? nextEvents[nextEvents.length - 1];
      const nextSettings: IntegrationSettings = {
        ...prev,
        eventStall: {
          ...prev.eventStall,
          events: nextEvents,
        },
      };
      return applyActiveEventSnapshot(nextSettings, nextActive?.id);
    });
  };

  const eventSoldQuantityByItemId = React.useMemo(() => {
    const map = new Map<number, number>();
    const profileId = activeEventProfile?.id;
    orders
      .filter((o) => o.orderType === 'event' && o.status !== 'cancelled' && o.status !== 'refunded')
      .filter((o) => orderMatchesEventProfile(o, profileId))
      .forEach((order) => {
        (order.items ?? []).forEach((line) => {
          if (line.removed) return;
          const itemId = Number(line.id);
          if (!Number.isFinite(itemId)) return;
          map.set(itemId, (map.get(itemId) ?? 0) + (Number(line.quantity) || 0));
        });
      });
    return map;
  }, [orders, activeEventProfile?.id]);


  useEffect(() => {
    settingsMainRef.current?.scrollTo({ top: 0 });
  }, [activeSection]);

  useEffect(() => {
    setCurrentSettings(ensureSettingsDefaults(settings));
  }, [settings]);

  useEffect(() => {
    const r = ensureSettingsDefaults(settings).eventStall?.eventTokenRollMax;
    setEventTokenPadDraft(r != null && r >= 2 ? String(r) : '');
  }, [settings]);

  const upsertEventMenuItem = () => {
    const name = eventMenuDraft.name.trim();
    const categoryName = eventMenuDraft.categoryName.trim();
    const price = Number(eventMenuDraft.price);
    if (!name) {
      setEventMenuError('Item name is required.');
      return;
    }
    if (!categoryName) {
      setEventMenuError('Category name is required.');
      return;
    }
    if (!Number.isFinite(price) || price <= 0) {
      setEventMenuError('Event price must be greater than 0.');
      return;
    }

    const roundedPrice = Math.round(price * 100) / 100;
    const parsedOpeningQty = eventMenuDraft.openingQuantity.trim() === ''
      ? undefined
      : Math.max(0, Math.floor(Number(eventMenuDraft.openingQuantity)));
    if (eventMenuDraft.openingQuantity.trim() !== '' && (!Number.isFinite(Number(eventMenuDraft.openingQuantity)) || Number(eventMenuDraft.openingQuantity) < 0)) {
      setEventMenuError('Opening quantity must be 0 or greater.');
      return;
    }
    let parsedUnitCost: number | undefined;
    if (eventMenuDraft.unitCost.trim() !== '') {
      const u = Math.round(Number(eventMenuDraft.unitCost) * 100) / 100;
      if (!Number.isFinite(u) || u < 0) {
        setEventMenuError('Unit cost must be a non-negative number.');
        return;
      }
      parsedUnitCost = u;
    }
    const payload: EventStallMenuItem = {
      id: editingEventMenuItemId ?? Date.now(),
      name,
      categoryName,
      price: roundedPrice,
      imageUrl: eventMenuDraft.imageUrl.trim() || undefined,
      isAvailable: eventMenuDraft.isAvailable,
      openingQuantity: parsedOpeningQty,
      displayOrder: 0,
      ...(parsedUnitCost !== undefined ? { unitCost: parsedUnitCost } : {}),
    };

    syncActiveEventInState((active) => {
      const items = [...(active.menuSnapshot ?? [])];
      const next =
        editingEventMenuItemId == null
          ? [...items, { ...payload, displayOrder: items.length }]
          : items.map((item, index) => {
              if (item.id !== editingEventMenuItemId) return item;
              const merged: EventStallMenuItem = {
                ...item,
                ...payload,
                displayOrder: item.displayOrder ?? index,
              };
              if (parsedUnitCost !== undefined) merged.unitCost = parsedUnitCost;
              else delete merged.unitCost;
              return merged;
            });
      const nextNames = [...new Set(next.map((i) => String(i.categoryName ?? '').trim()).filter(Boolean))];
      return {
        ...active,
        menuSnapshot: next,
        eventCategoryOrderSnapshot: mergeEventCategoryOrder(nextNames, active.eventCategoryOrderSnapshot ?? []),
      };
    });

    setEventMenuDraft(emptyEventMenuDraft);
    setEditingEventMenuItemId(null);
    setEventMenuError(null);
  };

  const startEditEventMenuItem = (item: EventStallMenuItem) => {
    setEditingEventMenuItemId(item.id);
    setEventMenuDraft({
      name: item.name,
      categoryName: item.categoryName,
      price: String(item.price),
      unitCost: item.unitCost != null && Number.isFinite(item.unitCost) ? String(item.unitCost) : '',
      openingQuantity: item.openingQuantity != null ? String(item.openingQuantity) : '',
      imageUrl: item.imageUrl ?? '',
      isAvailable: item.isAvailable !== false,
    });
    setEventMenuError(null);
  };

  const moveEventCategoryOrder = (categoryName: string, dir: -1 | 1) => {
    const items = activeEventProfile?.menuSnapshot ?? currentSettings.eventStall?.eventMenuItems ?? [];
    const names = [...new Set(items.map((i) => String(i.categoryName ?? '').trim()).filter(Boolean))];
    const ordered = mergeEventCategoryOrder(
      names,
      activeEventProfile?.eventCategoryOrderSnapshot ?? currentSettings.eventStall?.eventCategoryOrder ?? []
    );
    const idx = ordered.indexOf(categoryName);
    if (idx < 0) return;
    const j = idx + dir;
    if (j < 0 || j >= ordered.length) return;
    const nextOrder = [...ordered];
    [nextOrder[idx], nextOrder[j]] = [nextOrder[j], nextOrder[idx]];
    syncActiveEventInState((active) => ({
      ...active,
      eventCategoryOrderSnapshot: nextOrder,
    }));
  };

  const removeEventMenuItem = (id: number) => {
    syncActiveEventInState((active) => {
      const remaining = (active.menuSnapshot ?? [])
        .filter((item) => item.id !== id)
        .map((item, index) => ({ ...item, displayOrder: index }));
      const names = [...new Set(remaining.map((i) => String(i.categoryName ?? '').trim()).filter(Boolean))];
      return {
        ...active,
        menuSnapshot: remaining,
        eventCategoryOrderSnapshot: mergeEventCategoryOrder(names, active.eventCategoryOrderSnapshot ?? []),
      };
    });
    if (editingEventMenuItemId === id) {
      setEditingEventMenuItemId(null);
      setEventMenuDraft(emptyEventMenuDraft);
      setEventMenuError(null);
    }
  };

  const replenishEventMenuItem = (item: EventStallMenuItem) => {
    const currentOpening = typeof item.openingQuantity === 'number' ? Math.max(0, Math.floor(item.openingQuantity)) : null;
    const promptLabel = currentOpening == null
      ? 'Set opening quantity for this item (current: Unlimited):'
      : `Add quantity to ${item.name} (current opening: ${currentOpening}):`;
    const raw = window.prompt(promptLabel, currentOpening == null ? '0' : '10');
    if (raw == null) return;
    const amount = Number(raw);
    if (!Number.isFinite(amount) || amount < 0) {
      alert('Please enter a valid number (0 or greater).');
      return;
    }
    const addQty = Math.floor(amount);
    syncActiveEventInState((active) => {
      const soldQtyForRow = (id: number) => {
        let sold = 0;
        orders
          .filter((o) => o.orderType === 'event' && o.status !== 'cancelled' && o.status !== 'refunded')
          .forEach((order) => {
            (order.items ?? []).forEach((line) => {
              if (line.removed) return;
              if (Number(line.id) !== id) return;
              sold += Number(line.quantity) || 0;
            });
          });
        return sold;
      };
      const nextItems = (active.menuSnapshot ?? []).map((row) => {
        if (row.id !== item.id) return row;
        const existing = typeof row.openingQuantity === 'number' ? Math.max(0, Math.floor(row.openingQuantity)) : 0;
        const nextOpening = (currentOpening == null ? 0 : existing) + addQty;
        const remaining = nextOpening - soldQtyForRow(row.id);
        return {
          ...row,
          openingQuantity: nextOpening,
          ...(remaining > 0 ? { soldOut: false } : {}),
        };
      });
      return {
        ...active,
        menuSnapshot: nextItems,
      };
    });
  };

  const toggleEventMenuSoldOut = (item: EventStallMenuItem) => {
    syncActiveEventInState((active) => ({
      ...active,
      menuSnapshot: (active.menuSnapshot ?? []).map((row) =>
        row.id === item.id ? { ...row, soldOut: !row.soldOut } : row,
      ),
    }));
  };

  const addEventOperatorRow = async () => {
    const name = newEventOperatorName.trim();
    const pin = newEventOperatorPin.trim();
    if (!name) {
      alert('Operator name is required.');
      return;
    }
    if (pin.length < 4) {
      alert('PIN must be at least 4 digits/characters.');
      return;
    }
    const salt = randomPinSalt();
    const pinHash = await hashEventOperatorPin(pin, salt);
    const id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `op-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const row: EventStallOperator = { id, name, pinSalt: salt, pinHash };
    setCurrentSettings((prev) => ({
      ...prev,
      eventStall: {
        ...prev.eventStall,
        eventOperators: [...(prev.eventStall?.eventOperators ?? []), row],
      },
    }));
    setNewEventOperatorName('');
    setNewEventOperatorPin('');
  };

  const removeEventOperatorRow = (id: string) => {
    setCurrentSettings((prev) => ({
      ...prev,
      eventStall: {
        ...prev.eventStall,
        eventOperators: (prev.eventStall?.eventOperators ?? []).filter((o) => o.id !== id),
      },
    }));
  };

  const resetEventOperatorPin = async (op: EventStallOperator) => {
    const pin = window.prompt(`New PIN for ${op.name} (min 4 characters):`, '');
    if (pin == null) return;
    const trimmed = pin.trim();
    if (trimmed.length < 4) {
      alert('PIN must be at least 4 characters.');
      return;
    }
    const salt = randomPinSalt();
    const pinHash = await hashEventOperatorPin(trimmed, salt);
    setCurrentSettings((prev) => ({
      ...prev,
      eventStall: {
        ...prev.eventStall,
        eventOperators: (prev.eventStall?.eventOperators ?? []).map((o) =>
          o.id === op.id ? { ...o, pinSalt: salt, pinHash } : o,
        ),
      },
    }));
  };

  const handleDeliveryChange = (platform: DeliveryPlatform, key: string, value: any) => {
      setCurrentSettings(prev => ({ ...prev, [platform]: { ...(prev[platform] || {}), [key]: value } }));
  };

  const handleDeliveryApproval = (platform: DeliveryPlatform, status: IntegrationApproval['status'], notes?: string) => {
    const existingApproval = currentSettings[platform]?.approval;
    const approval: IntegrationApproval = {
      status,
      notes,
      testedBy: (status === 'testing' || status === 'approved' || status === 'rejected') ? currentUser?.id : existingApproval?.testedBy,
      testedAt: (status === 'testing' || status === 'approved' || status === 'rejected') ? Date.now() : existingApproval?.testedAt,
      approvedBy: status === 'approved' ? currentUser?.id : existingApproval?.approvedBy,
      approvedAt: status === 'approved' ? Date.now() : existingApproval?.approvedAt,
      thirdPartyName: platform,
    };
    
    setCurrentSettings(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        approval,
      },
    }));
  };

  const handleSmartpayApproval = (status: IntegrationApproval['status'], notes?: string) => {
    const existingApproval = currentSettings.smartpay?.approval;
    const approval: IntegrationApproval = {
      status,
      notes,
      testedBy: (status === 'testing' || status === 'approved' || status === 'rejected') ? currentUser?.id : existingApproval?.testedBy,
      testedAt: (status === 'testing' || status === 'approved' || status === 'rejected') ? Date.now() : existingApproval?.testedAt,
      approvedBy: status === 'approved' ? currentUser?.id : existingApproval?.approvedBy,
      approvedAt: status === 'approved' ? Date.now() : existingApproval?.approvedAt,
      thirdPartyName: 'SmartPay',
    };
    
    setCurrentSettings(prev => ({
      ...prev,
      smartpay: {
        ...prev.smartpay,
        approval,
      },
    }));
  };

  const handleSave = async () => {
    const businessHours = normalizeBusinessHours(currentSettings.businessHours || defaultBusinessHours);
    const hoursError = validateBusinessHours(businessHours);
    if (hoursError) {
      setSaveNotice({ type: 'error', text: hoursError });
      return;
    }
    const holidaySurcharge =
      normalizeHolidaySurchargeConfig(currentSettings.holidaySurcharge) ?? {
        enabled: false,
        percent: 0,
        dates: [],
      };
    try {
      await Promise.resolve(onSave({ ...currentSettings, businessHours, holidaySurcharge }));
      const onlineSummary = onlineSettingsSummary({
        beforeEnabled: settings.businessInfo?.onlineOrderingEnabled === true,
        afterEnabled: currentSettings.businessInfo?.onlineOrderingEnabled === true,
        hoursChanged:
          JSON.stringify(normalizeBusinessHours(settings.businessHours || defaultBusinessHours)) !==
          JSON.stringify(businessHours),
      });
      const summary = settingsAuditSummary(
        settings as unknown as Record<string, unknown>,
        { ...currentSettings, businessHours, holidaySurcharge } as unknown as Record<string, unknown>,
        onlineSummary,
      );
      if (summary) {
        writeClientAudit({
          action: onlineSummary && summary === onlineSummary ? 'settings.online_ordering_changed' : 'settings.saved',
          entityType: 'settings',
          entityId: 'settings',
          summary,
        });
      }
      setSaveNotice({ type: 'success', text: 'Settings saved.' });
    } catch {
      setSaveNotice({ type: 'error', text: 'Could not save settings. Please try again.' });
    }
  };

  const handleUploadKitchenAlertSound = async (file: File | null) => {
    if (!file) return;
    try {
      setIsUploadingAlertSound(true);
      const url = await uploadAlertSound(file);
      setCurrentSettings((prev) => ({
        ...prev,
        kitchenSLA: {
          ...prev.kitchenSLA,
          alertSoundUrl: url,
        },
      }));
      alert('Kitchen alert sound uploaded. Click Save Settings to apply.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to upload alert sound.';
      alert(message);
    } finally {
      setIsUploadingAlertSound(false);
    }
  };

  const handleUploadInvoiceLogo = async (file: File | null) => {
    if (!file) return;
    try {
      setIsUploadingInvoiceLogo(true);
      const { url, dataUrl } = await uploadInvoiceLogo(file);
      setCurrentSettings((prev) => ({
        ...prev,
        purchasingInvoiceDefaults: {
          ...prev.purchasingInvoiceDefaults,
          invoiceLogoUrl: url,
          invoiceLogoDataUrl: dataUrl,
        },
      }));
      alert('Invoice logo uploaded. Click Save Settings to apply.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to upload invoice logo.';
      alert(message);
    } finally {
      setIsUploadingInvoiceLogo(false);
    }
  };

  const handleTestKitchenAlertSound = () => {
    const soundUrl = resolveKitchenAlertSoundUrl(currentSettings.kitchenSLA?.alertSoundUrl);
    const volPct = currentSettings.kitchenSLA?.alertSoundVolume ?? 100;
    const volume = Math.min(1, Math.max(0, volPct / 100));
    try {
      const audio = new Audio(soundUrl);
      audio.volume = volume;
      audio.play().catch(() => { /* autoplay / interrupted - test playback */ });
    } catch {
      alert('Unable to play sound on this browser.');
    }
  };

  const handleBusinessHourChange = (day: Weekday, updates: Partial<BusinessHoursDay>) => {
    setCurrentSettings((prev) => {
      const existing = prev.businessHours || defaultBusinessHours;
      const updatedDays = weekDayOrder.map((weekday) => {
        const dayConfig =
          existing.days.find((d) => d.day === weekday) || { day: weekday, open: null, close: null, closed: true };
        if (weekday !== day) return dayConfig;
        if (updates.closed === true) {
          return {
            ...dayConfig,
            notes: updates.notes ?? dayConfig.notes,
            closed: true,
            open: null,
            close: null,
            periods: [],
          };
        }
        if (updates.periods) {
          const periods = updates.periods;
          const envelope = envelopeFromPeriods(periods.filter((period) => period.open && period.close));
          return {
            ...dayConfig,
            notes: updates.notes ?? dayConfig.notes,
            closed: false,
            periods,
            open: envelope.open,
            close: envelope.close,
          };
        }
        if (updates.closed === false) {
          const existingPeriods = resolveDayPeriods({ ...dayConfig, closed: false });
          const periods = existingPeriods.length > 0 ? existingPeriods : [{ open: '09:00', close: '17:00' }];
          const envelope = envelopeFromPeriods(periods);
          return {
            ...dayConfig,
            notes: updates.notes ?? dayConfig.notes,
            closed: false,
            periods,
            open: envelope.open,
            close: envelope.close,
          };
        }
        return { ...dayConfig, ...updates };
      });
      return {
        ...prev,
        businessHours: {
          ...existing,
          days: updatedDays,
        },
      };
    });
  };

  const handleTimezoneChange = (value: string) => {
    setCurrentSettings((prev) => ({
      ...prev,
      businessHours: {
        ...(prev.businessHours || defaultBusinessHours),
        timezone: value,
      },
    }));
  };

  return (
    <>
    <div className="flex flex-1 h-full min-h-0 bg-slate-50">
      <aside className="hidden lg:flex w-60 shrink-0 flex-col border-r border-slate-200 bg-white">
        <div className="px-4 pt-5 pb-3">
          <h1 className="text-lg font-semibold tracking-tight text-slate-900">Settings</h1>
          <label className="mt-3 block">
            <span className="sr-only">Find a setting</span>
            <input
              type="search"
              value={sectionQuery}
              onChange={(e) => setSectionQuery(e.target.value)}
              placeholder="Find a setting"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-pink-500/30 focus:border-pink-400"
            />
          </label>
        </div>
        <nav className="flex-1 overflow-y-auto px-2 pb-4" aria-label="Settings sections">
          {settingsNav.length === 0 ? (
            <p className="px-2 py-3 text-sm text-slate-500">No matching settings.</p>
          ) : (
            settingsNav.map((group) => (
              <div key={group.label} className="mb-4">
                <p className="px-2.5 mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{group.label}</p>
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const selected = activeSection === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        aria-current={selected ? 'page' : undefined}
                        onClick={() => {
                          setActiveSection(item.id);
                          setSectionQuery('');
                        }}
                        className={`w-full text-left px-2.5 py-1.5 rounded-md text-sm transition-[background-color,color,transform] duration-150 ease-out active:scale-[0.98] ${
                          selected
                            ? 'bg-pink-50 text-pink-700 font-medium'
                            : 'text-slate-700 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-slate-50'
                        }`}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </nav>
      </aside>
      <div className="flex flex-col flex-1 min-w-0 min-h-0">
      <header className="bg-white/90 backdrop-blur-sm border-b border-slate-200 z-10 flex-shrink-0">
        <div className="pl-4 pr-14 sm:pl-6 sm:pr-16">
          <div className="flex items-center justify-between gap-3 h-14">
            <div className="min-w-0 lg:hidden">
              <h1 className="text-lg font-semibold text-slate-900">Settings</h1>
            </div>
            <p className="hidden lg:block text-sm text-slate-500 truncate">Changes apply after you save.</p>
            <button type="button" onClick={handleSave} className="shrink-0 bg-pink-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-pink-600 transition-[background-color,transform] duration-150 ease-out active:scale-[0.98]">Save Changes</button>
          </div>
          <div className="lg:hidden pb-3">
            <label className="block">
              <span className="sr-only">Settings section</span>
              <select
                value={activeSection}
                onChange={(e) => setActiveSection(e.target.value as SettingsSectionId)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-pink-500/30 focus:border-pink-400"
              >
                {visibleSettingsNav(isAdmin, showMenuPhotos, '').map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.items.map((item) => (
                      <option key={item.id} value={item.id}>{item.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
          </div>
          {saveNotice && (
            <div
              role="status"
              className={`-ml-4 -mr-14 sm:-ml-6 sm:-mr-16 px-4 sm:px-6 py-2 text-sm font-medium ${
                saveNotice.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}
            >
              {saveNotice.text}
            </div>
          )}
        </div>
      </header>
      <main ref={settingsMainRef} className="flex-1 p-4 sm:p-6 overflow-y-auto">
        <div className="w-full">
          
          <div className={isSection('hardware') ? undefined : 'hidden'}>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Hardware & Payments</h2>
            <div className="space-y-8">
                <SmartpaySettingsComponent 
                  settings={currentSettings} 
                  onUpdateTerminal={onUpdateTerminal} 
                  onAddTerminal={onAddTerminal} 
                  onDeleteTerminal={onDeleteTerminal}
                  currentUser={currentUser}
                  onUpdateApproval={handleSmartpayApproval}
                />
                {/* Receipt Printers */}
                <div className="bg-white shadow rounded-lg">
                    <div className="px-6 py-4 border-b flex justify-between items-center">
                        <h3 className="text-xl font-bold text-gray-800">Receipt Printers</h3>
                        <button onClick={() => { setEditingPrinter(null); setIsPrinterModalOpen(true); }} className="bg-blue-500 text-white font-semibold py-1 px-3 rounded-md text-sm hover:bg-blue-600 flex items-center"><PlusIcon className="w-4 h-4 mr-1"/>Add New</button>
                    </div>
                     <div className="p-6">
                        {settings.receiptPrinters.map(printer => (
                            <div key={printer.id} className="flex justify-between items-center gap-3 py-3 border-b border-slate-100 last:border-0">
                                <p className="text-sm font-medium text-slate-800">{printer.name}</p>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => { setEditingPrinter(printer); setIsPrinterModalOpen(true); }} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-pink-600"><EditIcon className="w-4 h-4" /></button>
                                    <button onClick={() => onDeletePrinter(printer.id)} className="p-1.5 rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600"><TrashIcon className="w-4 h-4" /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
          </div>

          {/* Business Information */}
          <div className={`${isSection('business') ? '' : 'hidden '}bg-white shadow-sm border border-slate-200 rounded-xl p-6 space-y-4`}>
            <h2 className="text-2xl font-semibold text-gray-900 mb-1">Business Information</h2>
            <p className="text-sm text-gray-500 mb-4">
              This information will appear on receipts, invoices, and customer emails.
            </p>
            <div className="space-y-4">
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Name *
                </label>
                <input
                  type="text"
                  value={currentSettings.businessInfo?.businessName || ''}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      businessInfo: {
                        ...prev.businessInfo || { businessName: '', address: '', gstNumber: '' },
                        businessName: e.target.value,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                  required
                />
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address *
                </label>
                <input
                  type="text"
                  value={currentSettings.businessInfo?.address || ''}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      businessInfo: {
                        ...prev.businessInfo || { businessName: '', address: '', gstNumber: '' },
                        address: e.target.value,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                  required
                />
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GST Number *
                </label>
                <input
                  type="text"
                  value={currentSettings.businessInfo?.gstNumber || ''}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      businessInfo: {
                        ...prev.businessInfo || { businessName: '', address: '', gstNumber: '' },
                        gstNumber: e.target.value,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                  required
                />
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  NZBN (optional)
                </label>
                <input
                  type="text"
                  value={currentSettings.businessInfo?.nzbn || ''}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      businessInfo: {
                        ...prev.businessInfo || { businessName: '', address: '', gstNumber: '' },
                        nzbn: e.target.value,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                  placeholder="e.g. 9429XXXXXXXXXX"
                />
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={currentSettings.businessInfo?.phone || ''}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      businessInfo: {
                        ...prev.businessInfo || { businessName: '', address: '', gstNumber: '' },
                        phone: e.target.value,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                />
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={currentSettings.businessInfo?.email || ''}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      businessInfo: {
                        ...prev.businessInfo || { businessName: '', address: '', gstNumber: '' },
                        email: e.target.value,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                />
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  value={currentSettings.businessInfo?.website || ''}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      businessInfo: {
                        ...prev.businessInfo || { businessName: '', address: '', gstNumber: '' },
                        website: e.target.value,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                />
              </div>

              <div className="border-t border-gray-200 pt-4 mt-2">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Purchasing invoices</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Defaults for purchase orders and supplier tax invoices.
                </p>
                <div className="space-y-4">
                  <div className="settings-field">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default GST pricing
                    </label>
                    <select
                      value={currentSettings.purchasingInvoiceDefaults?.defaultGstMode || 'inclusive'}
                      onChange={(e) =>
                        setCurrentSettings((prev) => ({
                          ...prev,
                          purchasingInvoiceDefaults: {
                            ...prev.purchasingInvoiceDefaults,
                            defaultGstMode: e.target.value as 'inclusive' | 'exclusive',
                          },
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    >
                      <option value="inclusive">GST inclusive</option>
                      <option value="exclusive">GST exclusive</option>
                    </select>
                  </div>
                  <div className="settings-field">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default payment terms
                    </label>
                    <input
                      type="text"
                      value={currentSettings.purchasingInvoiceDefaults?.defaultPaymentTerms || ''}
                      onChange={(e) =>
                        setCurrentSettings((prev) => ({
                          ...prev,
                          purchasingInvoiceDefaults: {
                            ...prev.purchasingInvoiceDefaults,
                            defaultPaymentTerms: e.target.value,
                          },
                        }))
                      }
                      placeholder="e.g. 20th of month following"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    />
                  </div>
                  <div className="settings-field settings-span">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client invoice logo (optional)
                    </label>
                    <p className="mb-2 text-xs text-gray-500">
                      Shown on client tax invoices only. If empty, the email/booking logo below is used instead.
                    </p>
                    {currentSettings.purchasingInvoiceDefaults?.invoiceLogoUrl &&
                    !currentSettings.purchasingInvoiceDefaults?.invoiceLogoDataUrl ? (
                      <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        PDF preview needs an embedded copy of the logo. Click <strong>Upload logo</strong> again
                        (same file is fine), then <strong>Save Settings</strong>.
                      </p>
                    ) : null}
                    <input
                      ref={invoiceLogoFileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0] ?? null;
                        void handleUploadInvoiceLogo(file);
                        e.currentTarget.value = '';
                      }}
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => invoiceLogoFileInputRef.current?.click()}
                        disabled={isUploadingInvoiceLogo}
                        className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
                      >
                        {isUploadingInvoiceLogo ? 'Uploading…' : 'Upload logo'}
                      </button>
                      {currentSettings.purchasingInvoiceDefaults?.invoiceLogoUrl ? (
                        <button
                          type="button"
                          onClick={() =>
                            setCurrentSettings((prev) => ({
                              ...prev,
                              purchasingInvoiceDefaults: {
                                ...prev.purchasingInvoiceDefaults,
                                invoiceLogoUrl: '',
                                invoiceLogoDataUrl: '',
                              },
                            }))
                          }
                          className="text-sm text-red-600 hover:text-red-700"
                        >
                          Remove logo
                        </button>
                      ) : null}
                    </div>
                    <p className="text-xs text-gray-500 mt-2 mb-1">Or paste a direct image URL:</p>
                    <input
                      type="url"
                      value={currentSettings.purchasingInvoiceDefaults?.invoiceLogoUrl || ''}
                      onChange={(e) =>
                        setCurrentSettings((prev) => ({
                          ...prev,
                          purchasingInvoiceDefaults: {
                            ...prev.purchasingInvoiceDefaults,
                            invoiceLogoUrl: e.target.value,
                            invoiceLogoDataUrl: '',
                          },
                        }))
                      }
                      placeholder="https://… PNG/JPG (optional if you uploaded above)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    />
                    {currentSettings.purchasingInvoiceDefaults?.invoiceLogoUrl ? (
                      <div className="mt-2 rounded-md border border-gray-200 overflow-hidden bg-gray-50 p-3 inline-block">
                        <img
                          src={getInvoiceLogoDisplayUrl(currentSettings.purchasingInvoiceDefaults.invoiceLogoUrl)}
                          alt="Invoice logo preview"
                          className="max-h-16 max-w-[200px] w-auto h-auto object-contain"
                          onError={(ev) => {
                            (ev.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                  <div className="settings-field">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bank account name
                    </label>
                    <p className="mb-1 text-xs text-gray-500">Shown on client tax invoices for payment.</p>
                    <input
                      type="text"
                      value={currentSettings.purchasingInvoiceDefaults?.bankAccountName || ''}
                      onChange={(e) =>
                        setCurrentSettings((prev) => ({
                          ...prev,
                          purchasingInvoiceDefaults: {
                            ...prev.purchasingInvoiceDefaults,
                            bankAccountName: e.target.value,
                          },
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    />
                  </div>
                  <div className="settings-field">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bank account number
                    </label>
                    <input
                      type="text"
                      value={currentSettings.purchasingInvoiceDefaults?.bankAccountNumber || ''}
                      onChange={(e) =>
                        setCurrentSettings((prev) => ({
                          ...prev,
                          purchasingInvoiceDefaults: {
                            ...prev.purchasingInvoiceDefaults,
                            bankAccountNumber: e.target.value,
                          },
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    />
                  </div>
                  <div className="border-t border-gray-200 pt-4 mt-2">
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">Payment planner</h4>
                    <p className="text-sm text-gray-500 mb-3">
                      Advisory defaults for Purchasing → Payments due. Uses daily sales summaries (not live orders) unless staff loads a breakdown.
                    </p>
                    <div>
                      <div className="settings-field">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Sales lookback (days)
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={30}
                          value={currentSettings.purchasingInvoiceDefaults?.paymentPlannerLookbackDays ?? 7}
                          onChange={(e) =>
                            setCurrentSettings((prev) => ({
                              ...prev,
                              purchasingInvoiceDefaults: {
                                ...prev.purchasingInvoiceDefaults,
                                paymentPlannerLookbackDays: Math.min(
                                  30,
                                  Math.max(1, Number(e.target.value) || 7),
                                ),
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                        />
                      </div>
                      <div className="settings-field">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Safety factor (0.1–1)
                        </label>
                        <input
                          type="number"
                          min={0.1}
                          max={1}
                          step={0.05}
                          value={currentSettings.purchasingInvoiceDefaults?.paymentPlannerSafetyFactor ?? 0.7}
                          onChange={(e) =>
                            setCurrentSettings((prev) => ({
                              ...prev,
                              purchasingInvoiceDefaults: {
                                ...prev.purchasingInvoiceDefaults,
                                paymentPlannerSafetyFactor: Math.min(
                                  1,
                                  Math.max(0.1, Number(e.target.value) || 0.7),
                                ),
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Fraction of estimated spendable sales used for bill suggestions.
                        </p>
                      </div>
                      <div className="settings-field">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Card liquidity (%)
                        </label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={
                            currentSettings.purchasingInvoiceDefaults?.paymentPlannerCardLiquidityPercent ??
                            100
                          }
                          onChange={(e) =>
                            setCurrentSettings((prev) => ({
                              ...prev,
                              purchasingInvoiceDefaults: {
                                ...prev.purchasingInvoiceDefaults,
                                paymentPlannerCardLiquidityPercent: Math.min(
                                  100,
                                  Math.max(0, Number(e.target.value) || 0),
                                ),
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          When order breakdown is loaded, % of card sales counted as spendable.
                        </p>
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                          <input
                            type="checkbox"
                            checked={
                              currentSettings.purchasingInvoiceDefaults?.paymentPlannerCashOnly ?? false
                            }
                            onChange={(e) =>
                              setCurrentSettings((prev) => ({
                                ...prev,
                                purchasingInvoiceDefaults: {
                                  ...prev.purchasingInvoiceDefaults,
                                  paymentPlannerCashOnly: e.target.checked,
                                },
                              }))
                            }
                            className="rounded border-gray-300"
                          />
                          Cash-only budget (when breakdown loaded)
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Logo URL — emails and booking (optional)
                </label>
                <input
                  type="url"
                  value={currentSettings.businessInfo?.emailLogoUrl || ''}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      businessInfo: {
                        ...prev.businessInfo || { businessName: '', address: '', gstNumber: '' },
                        emailLogoUrl: e.target.value,
                      },
                    }))
                  }
                  placeholder="https://… PNG/SVG hosted logo (shown on event booking page header)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                />
              </div>

              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Footer (Line 1)
                </label>
                <input
                  type="text"
                  value={currentSettings.businessInfo?.emailFooterLine1 || ''}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      businessInfo: {
                        ...prev.businessInfo || { businessName: '', address: '', gstNumber: '' },
                        emailFooterLine1: e.target.value,
                      },
                    }))
                  }
                  placeholder="e.g. Thank you for supporting Kings Park."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 text-sm"
                />
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Footer (Line 2)
                </label>
                <input
                  type="text"
                  value={currentSettings.businessInfo?.emailFooterLine2 || ''}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      businessInfo: {
                        ...prev.businessInfo || { businessName: '', address: '', gstNumber: '' },
                        emailFooterLine2: e.target.value,
                      },
                    }))
                  }
                  placeholder="e.g. Visit us again soon!"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 text-sm"
                />
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Google Review Link
                </label>
                <input
                  type="url"
                  value={currentSettings.businessInfo?.googleReviewUrl || ''}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      businessInfo: {
                        ...prev.businessInfo || { businessName: '', address: '', gstNumber: '' },
                        googleReviewUrl: e.target.value,
                      },
                    }))
                  }
                  placeholder="Paste your Google review URL here"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  This URL will be used for the Google review QR code and links in emails.
                </p>
              </div>

              <div className="border-t border-gray-200 pt-4 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800">External apps (optional)</h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Configure links to other apps you host separately. Detailed Reports can open Statement and Payroll in a new tab.
                  </p>
                </div>
                <div className="settings-field">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Statement app URL
                  </label>
                  <input
                    type="url"
                    value={currentSettings.externalStatementAppUrl || ''}
                    onChange={(e) =>
                      setCurrentSettings((prev) => ({
                        ...prev,
                        externalStatementAppUrl: e.target.value,
                      }))
                    }
                    placeholder="https://…"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 text-sm"
                  />
                </div>
                <div className="settings-field">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payroll app URL
                  </label>
                  <input
                    type="url"
                    value={currentSettings.externalPayrollAppUrl || ''}
                    onChange={(e) =>
                      setCurrentSettings((prev) => ({
                        ...prev,
                        externalPayrollAppUrl: e.target.value,
                      }))
                    }
                    placeholder="https://…"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Online Ordering */}
          <div className={`${isSection('online') ? '' : 'hidden '}bg-white shadow-sm border border-slate-200 rounded-xl p-6 space-y-4`}>
            <h2 className="text-2xl font-semibold text-gray-900 mb-1">Online Ordering</h2>
            <p className="text-sm text-gray-500 mb-4">
              Share this link on your website or social media to allow customers to place orders online.
            </p>
            <div className="space-y-4">
              <div className="settings-switch">
                <div>
                  <label htmlFor="onlineOrderingEnabled" className="text-sm font-medium text-slate-800">
                    Enable online ordering
                  </label>
                  <p className="mt-0.5 text-xs text-slate-500">
                    When disabled, the online ordering link and QR code will show a “currently unavailable” message to customers.
                  </p>
                </div>
                <input
                  type="checkbox"
                  id="onlineOrderingEnabled"
                  checked={currentSettings.businessInfo?.onlineOrderingEnabled === true}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      businessInfo: {
                        ...prev.businessInfo || { businessName: '', address: '', gstNumber: '' },
                        onlineOrderingEnabled: e.target.checked,
                      },
                    }))
                  }
                  className="h-4 w-4 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                />
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Custom Domain (Optional)
                </label>
                <input
                  type="text"
                  value={currentSettings.businessInfo?.onlineOrderingCustomDomain || ''}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      businessInfo: {
                        ...prev.businessInfo || { businessName: '', address: '', gstNumber: '' },
                        onlineOrderingCustomDomain: e.target.value,
                      },
                    }))
                  }
                  placeholder="e.g., order.kingspark.co.nz (leave empty to use default)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  If you have a custom domain for online ordering, enter it here (without https://). Otherwise, the default URL will be used.
                </p>
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Production Ordering Link
                </label>
                <div className="flex flex-col items-start gap-2">
                  <input
                    type="text"
                    readOnly
                    value={(() => {
                      const customDomain = currentSettings.businessInfo?.onlineOrderingCustomDomain;
                      // If a dedicated online ordering domain is configured (e.g. order.kingspark.co.nz),
                      // use the root of that domain. Otherwise use /order on the current host.
                      if (customDomain && customDomain.trim()) {
                        return `https://${customDomain.trim()}/`;
                      }
                      return `${window.location.origin}/order`;
                    })()}
                    className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-900 text-sm font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const customDomain = currentSettings.businessInfo?.onlineOrderingCustomDomain;
                      const url = customDomain && customDomain.trim()
                        ? `https://${customDomain.trim()}/`
                        : `${window.location.origin}/order`;
                      navigator.clipboard.writeText(url).then(() => {
                        alert('Link copied to clipboard!');
                      }).catch(() => {
                        // Fallback for older browsers
                        const textArea = document.createElement('textarea');
                        textArea.value = url;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                        alert('Link copied to clipboard!');
                      });
                    }}
                    className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600 transition-colors text-sm font-semibold whitespace-nowrap"
                  >
                    Copy Link
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Use this link on your website, social media, or marketing materials. Customers can place pickup orders directly. If you configure a dedicated domain like order.kingspark.co.nz, that domain&apos;s home page will load the online ordering view.
                </p>
              </div>
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Ordering Hours</h3>
                <p className="text-sm text-gray-500">
                  Online ordering uses Business Hours below. Customers can order and pick up only during those open windows. Gaps between windows stay closed.
                </p>
              </div>
              <div className="settings-field settings-span">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  QR Code for Online Ordering
                </label>
                <div className="flex flex-wrap items-start gap-6">
                  <div className="flex-shrink-0">
                    <img
                      src={(() => {
                        const customDomain = currentSettings.businessInfo?.onlineOrderingCustomDomain;
                        const url = customDomain && customDomain.trim()
                          ? `https://${customDomain.trim()}/`
                          : `${window.location.origin}/order`;
                        return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;
                      })()}
                      alt="Online Ordering QR Code"
                      className="w-48 h-48 border-4 border-gray-200 rounded-lg"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-3">
                      Display this QR code at your restaurant entrance, on tables, or in marketing materials. Customers can scan it to start ordering.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        const customDomain = currentSettings.businessInfo?.onlineOrderingCustomDomain;
                        const url = customDomain && customDomain.trim()
                          ? `https://${customDomain.trim()}/`
                          : `${window.location.origin}/order`;
                        const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${encodeURIComponent(url)}`;
                        const link = document.createElement('a');
                        link.href = qrUrl;
                        link.download = 'online-ordering-qr-code.png';
                        link.click();
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors text-sm font-semibold"
                    >
                      Download QR Code
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Meal plans */}
          <div className={`${isSection('meal-plans') ? '' : 'hidden '}bg-white shadow-sm border border-slate-200 rounded-xl p-6 space-y-4`}>
            <h2 className="text-2xl font-semibold text-gray-900 mb-1">Meal plans</h2>
            <p className="text-sm text-gray-500 mb-4">
              Turn on student meal plans the same way as online ordering. Save settings after changing this switch.
            </p>
            <div className="settings-switch">
              <div>
                <label htmlFor="mealPlansEnabled" className="text-sm font-medium text-slate-800">
                  Enable meal plans
                </label>
                <p className="mt-0.5 text-xs text-slate-500">
                  When disabled, the order page hides Meal plan and the till does not offer Collect. Plan types, weekly menus, and subscribers stay saved.
                </p>
              </div>
              <input
                type="checkbox"
                id="mealPlansEnabled"
                checked={mealPlansOn}
                onChange={(e) =>
                  setCurrentSettings((prev) => ({
                    ...prev,
                    businessInfo: {
                      ...prev.businessInfo || { businessName: '', address: '', gstNumber: '' },
                      mealPlansEnabled: e.target.checked,
                    },
                  }))
                }
                className="h-4 w-4 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="font-medium text-slate-800">Online page heading</span>
                <p className="mt-0.5 text-xs text-slate-500">Shown at the top of the customer meal plan page.</p>
                <input
                  type="text"
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Student meal plans"
                  value={currentSettings.businessInfo?.mealPlanPublicTitle || ''}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      businessInfo: {
                        ...(prev.businessInfo || { businessName: '', address: '', gstNumber: '' }),
                        mealPlanPublicTitle: e.target.value,
                      },
                    }))
                  }
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-slate-800">Online button label</span>
                <p className="mt-0.5 text-xs text-slate-500">Short label on the order page header button.</p>
                <input
                  type="text"
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder="Meal plan"
                  value={currentSettings.businessInfo?.mealPlanNavLabel || ''}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      businessInfo: {
                        ...(prev.businessInfo || { businessName: '', address: '', gstNumber: '' }),
                        mealPlanNavLabel: e.target.value,
                      },
                    }))
                  }
                />
              </label>
            </div>
            {mealPlansOn && (
              <div className="border-t border-gray-200 pt-4">
                <MealPlansTab
                  customers={customers}
                  menuItems={menuItems}
                  categories={categories}
                  currentStaffId={currentUser?.id}
                  currentStaffName={currentUser?.name}
                  isManager={Boolean(currentUser?.isAdmin || currentUser?.role === 'manager')}
                />
              </div>
            )}
          </div>

          {/* Catering request alerts (customer app → email) */}
          <div className={`${isSection('catering') ? '' : 'hidden '}bg-white shadow-sm border border-slate-200 rounded-xl p-6 space-y-4`}>
            <h2 className="text-2xl font-semibold text-gray-900 mb-1">Catering request alerts</h2>
            <p className="text-sm text-gray-500 mb-4">
              Get an email whenever a customer requests a catering quote in the Kings Park app. Requests are also listed under
              Loyalty → Catering. Save settings after changing these.
            </p>
            <CateringAlertsFields
              value={currentSettings.cateringAlerts}
              onChange={(cateringAlerts) => setCurrentSettings((prev) => ({ ...prev, cateringAlerts }))}
            />
          </div>

          {/* Menu photos – clear all to start from scratch */}
          {onClearMenuPhotos && (
            <div className={`${isSection('menu-photos') ? '' : 'hidden '}bg-white shadow-sm border border-slate-200 rounded-xl p-6 space-y-4`}>
              <h2 className="text-2xl font-semibold text-gray-900 mb-1">Menu photos</h2>
              <p className="text-sm text-gray-500 mb-4">
                Remove all menu item photos from the POS and from Firebase Storage. Menu items will show &quot;No photo&quot; until you upload new images (e.g. low-quality photos).
              </p>
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm('Remove all menu photos from the POS and delete all uploaded images from Storage? This cannot be undone.')) return;
                  try {
                    await onClearMenuPhotos();
                    alert('All menu photos have been removed. You can now upload new images from Menu Management.');
                  } catch (err) {
                    console.error(err);
                    alert('Failed to clear menu photos. Please try again.');
                  }
                }}
                className="px-4 py-2 bg-amber-500 text-amber-900 rounded-md hover:bg-amber-600 font-semibold text-sm"
              >
                Clear all menu photos
              </button>
            </div>
          )}

          {/* Business Hours */}
          <div className={`${isSection('hours') ? '' : 'hidden '}bg-white shadow-sm border border-slate-200 rounded-xl p-6 space-y-4`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-1">Business Hours</h2>
                <p className="text-sm text-gray-500">
                  Add more than one open and close time when the day has gaps. These hours drive staff rostering, online ordering, and the Uber Eats menu. Re-sync the Uber menu after you change them. DoorDash hours are set in the DoorDash portal.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <label className="text-sm font-medium text-gray-700 flex flex-col">
                  Timezone
                  <input
                    type="text"
                    value={businessHours.timezone}
                    onChange={(e) => handleTimezoneChange(e.target.value)}
                    className="mt-1 w-full sm:w-60 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 text-sm"
                    placeholder="Pacific/Auckland"
                  />
                </label>
                <button
                  type="button"
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                  onClick={() => {
                    const monday = businessHours.days.find((d) => d.day === 'monday');
                    if (!monday) return;
                    weekDayOrder.forEach((day) => {
                      if (day === 'sunday') return;
                      if (monday.closed) {
                        handleBusinessHourChange(day, { closed: true });
                      } else {
                        handleBusinessHourChange(day, { periods: resolveDayPeriods(monday) });
                      }
                    });
                  }}
                >
                  Copy Monday to Weekdays
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Day</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Open windows</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {weekDayOrder.map((day) => {
                    const config =
                      businessHours.days.find((d) => d.day === day) || ({ day, open: null, close: null, closed: true } as BusinessHoursDay);
                    const label = day.charAt(0).toUpperCase() + day.slice(1);
                    const windows: BusinessHoursPeriod[] = config.closed
                      ? []
                      : config.periods && config.periods.length > 0
                        ? config.periods
                        : resolveDayPeriods(config);
                    const updateWindow = (index: number, patch: Partial<BusinessHoursPeriod>) => {
                      const next = windows.map((period, i) => (i === index ? { ...period, ...patch } : period));
                      handleBusinessHourChange(day, { periods: next });
                    };
                    return (
                      <tr key={day} className="border-b border-slate-100 last:border-0">
                        <td className="px-3 py-3 font-medium text-slate-800 align-top">{label}</td>
                        <td className="px-4 py-2 align-top">
                          <label className="inline-flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={config.closed ?? false}
                              onChange={(e) => handleBusinessHourChange(day, { closed: e.target.checked })}
                            />
                            Closed
                          </label>
                        </td>
                        <td className="px-4 py-2">
                          {config.closed ? (
                            <span className="text-gray-400">Closed</span>
                          ) : (
                            <div className="space-y-2">
                              {windows.map((period, index) => (
                                <div key={`${day}-${index}`} className="flex flex-wrap items-center gap-2">
                                  <input
                                    type="time"
                                    value={period.open || ''}
                                    onChange={(e) => updateWindow(index, { open: e.target.value })}
                                    className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-pink-500 focus:border-pink-500"
                                    aria-label={`${label} open ${index + 1}`}
                                  />
                                  <span className="text-gray-500">to</span>
                                  <input
                                    type="time"
                                    value={period.close || ''}
                                    onChange={(e) => updateWindow(index, { close: e.target.value })}
                                    className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-pink-500 focus:border-pink-500"
                                    aria-label={`${label} close ${index + 1}`}
                                  />
                                  {windows.length > 1 && (
                                    <button
                                      type="button"
                                      className="text-xs text-red-600 hover:text-red-800"
                                      onClick={() =>
                                        handleBusinessHourChange(day, {
                                          periods: windows.filter((_, i) => i !== index),
                                        })
                                      }
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                              ))}
                              {windows.length < MAX_BUSINESS_HOUR_PERIODS && (
                                <button
                                  type="button"
                                  className="text-xs font-semibold text-pink-600 hover:text-pink-800"
                                  onClick={() =>
                                    handleBusinessHourChange(day, {
                                      periods: [...windows, { open: '', close: '' }],
                                    })
                                  }
                                >
                                  Add open and close
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2 align-top">
                          <input
                            type="text"
                            value={config.notes || ''}
                            onChange={(e) => handleBusinessHourChange(day, { notes: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-pink-500 focus:border-pink-500"
                            placeholder="Optional note"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="border-t border-slate-200 pt-6 space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Public holiday surcharge</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Automatically adds a percentage to POS and online order totals on the dates you list
                  (using the timezone above). Uber/DoorDash orders are not included. Amounts are stored
                  on each order when it is placed or updated.
                </p>
              </div>
              <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-800">
                <input
                  type="checkbox"
                  checked={holidaySurcharge.enabled === true}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      holidaySurcharge: {
                        enabled: e.target.checked,
                        percent: prev.holidaySurcharge?.percent ?? 0,
                        dates: prev.holidaySurcharge?.dates ?? [],
                        label: prev.holidaySurcharge?.label,
                      },
                    }))
                  }
                  className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                />
                Enable holiday surcharge
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="holidaySurchargePercent" className="block text-sm font-medium text-gray-700 mb-1">
                    Percent
                  </label>
                  <input
                    id="holidaySurchargePercent"
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    value={holidaySurcharge.percent ?? 0}
                    onChange={(e) => {
                      const num = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                      setCurrentSettings((prev) => ({
                        ...prev,
                        holidaySurcharge: {
                          enabled: prev.holidaySurcharge?.enabled === true,
                          percent: num,
                          dates: prev.holidaySurcharge?.dates ?? [],
                          label: prev.holidaySurcharge?.label,
                        },
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">e.g. 15 for 15% of the bill (after discounts, including delivery fee).</p>
                </div>
                <div>
                  <label htmlFor="holidaySurchargeLabel" className="block text-sm font-medium text-gray-700 mb-1">
                    Receipt label
                  </label>
                  <input
                    id="holidaySurchargeLabel"
                    type="text"
                    maxLength={80}
                    value={holidaySurcharge.label ?? ''}
                    placeholder={DEFAULT_HOLIDAY_SURCHARGE_LABEL}
                    onChange={(e) =>
                      setCurrentSettings((prev) => ({
                        ...prev,
                        holidaySurcharge: {
                          enabled: prev.holidaySurcharge?.enabled === true,
                          percent: prev.holidaySurcharge?.percent ?? 0,
                          dates: prev.holidaySurcharge?.dates ?? [],
                          label: e.target.value,
                        },
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 text-sm"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="holidaySurchargeDateAdd" className="block text-sm font-medium text-gray-700 mb-1">
                  Holiday dates
                </label>
                <div className="flex flex-wrap gap-2 items-center">
                  <input
                    id="holidaySurchargeDateAdd"
                    type="date"
                    value={holidayDateDraft}
                    onChange={(e) => setHolidayDateDraft(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500 text-sm"
                  />
                  <button
                    type="button"
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                    onClick={() => {
                      const key = normalizeHolidayDateKey(holidayDateDraft);
                      if (!key) return;
                      setCurrentSettings((prev) => {
                        const existing = prev.holidaySurcharge?.dates ?? [];
                        if (existing.includes(key)) return prev;
                        return {
                          ...prev,
                          holidaySurcharge: {
                            enabled: prev.holidaySurcharge?.enabled === true,
                            percent: prev.holidaySurcharge?.percent ?? 0,
                            dates: [...existing, key].sort(),
                            label: prev.holidaySurcharge?.label,
                          },
                        };
                      });
                      setHolidayDateDraft('');
                    }}
                  >
                    Add date
                  </button>
                </div>
                {(holidaySurcharge.dates?.length ?? 0) === 0 ? (
                  <p className="text-xs text-gray-500 mt-2">No dates yet. Add each public holiday (e.g. Christmas, Waitangi Day) for the year.</p>
                ) : (
                  <ul className="mt-2 space-y-1">
                    {holidaySurcharge.dates.map((dateKey) => (
                      <li key={dateKey} className="flex items-center justify-between gap-2 text-sm text-slate-800 bg-slate-50 rounded-md px-3 py-1.5">
                        <span>{dateKey}</span>
                        <button
                          type="button"
                          className="text-xs text-red-600 hover:text-red-800"
                          onClick={() =>
                            setCurrentSettings((prev) => ({
                              ...prev,
                              holidaySurcharge: {
                                enabled: prev.holidaySurcharge?.enabled === true,
                                percent: prev.holidaySurcharge?.percent ?? 0,
                                dates: (prev.holidaySurcharge?.dates ?? []).filter((d) => d !== dateKey),
                                label: prev.holidaySurcharge?.label,
                              },
                            }))
                          }
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Kitchen SLA */}
          <div className={`${isSection('kitchen') ? '' : 'hidden '}bg-white shadow-sm border border-slate-200 rounded-xl p-6 space-y-4`}>
            <h2 className="text-2xl font-semibold text-gray-900 mb-1">Kitchen SLA</h2>
            <p className="text-sm text-gray-500 mb-4">
              Control how the Kitchen Display highlights tickets based on elapsed time.
            </p>
            <div className="settings-field settings-span">
              <div className="settings-field-label">
                <p className="text-sm font-medium text-slate-800">Kitchen Display text size</p>
                <p className="mt-0.5 text-xs text-slate-500">
                  Scales dish names, Done/Start buttons, headers, and special instructions together. One setting covers both line items and notes.
                </p>
              </div>
              <div className="flex flex-wrap gap-4 pt-1">
                {(['standard', 'large', 'xlarge'] as const).map((size) => (
                  <label key={size} className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="kitchenDisplayTextSize"
                      checked={(currentSettings.kitchenSLA?.kitchenDisplayTextSize ?? 'large') === size}
                      onChange={() =>
                        setCurrentSettings((prev) => ({
                          ...prev,
                          kitchenSLA: {
                            ...prev.kitchenSLA,
                            kitchenDisplayTextSize: size,
                          },
                        }))
                      }
                      className="h-4 w-4 text-pink-600 border-gray-300 focus:ring-pink-500"
                    />
                    <span className="text-sm text-gray-700 capitalize">{size === 'xlarge' ? 'Extra large' : size}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Warning Threshold (minutes)
                </label>
                <input
                  type="number"
                  min="1"
                  value={currentSettings.kitchenSLA?.warningMinutes ?? 5}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      kitchenSLA: {
                        ...prev.kitchenSLA,
                        warningMinutes: Math.max(1, Number(e.target.value)),
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Tickets are green until this threshold, then turn amber.
                </p>
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Critical Threshold (minutes)
                </label>
                <input
                  type="number"
                  min={(currentSettings.kitchenSLA?.warningMinutes ?? 5) + 1}
                  value={currentSettings.kitchenSLA?.dangerMinutes ?? 10}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      kitchenSLA: {
                        ...prev.kitchenSLA,
                        dangerMinutes: Math.max(
                          prev.kitchenSLA.warningMinutes + 1,
                          Number(e.target.value)
                        ),
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Tickets past this time turn red.
                </p>
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max minutes to start in kitchen
                </label>
                <input
                  type="number"
                  min="1"
                  value={currentSettings.kitchenSLA?.minutesToStart ?? 5}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      kitchenSLA: {
                        ...prev.kitchenSLA,
                        minutesToStart: Math.max(1, Number(e.target.value)),
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  SLA breach if order is still not started past this many minutes. Used in Executive Dashboard.
                </p>
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max minutes to serve (ready → served)
                </label>
                <input
                  type="number"
                  min="1"
                  value={currentSettings.kitchenSLA?.minutesToServe ?? 5}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      kitchenSLA: {
                        ...prev.kitchenSLA,
                        minutesToServe: Math.max(1, Number(e.target.value)),
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  SLA breach if order is ready for pickup but not served past this many minutes.
                </p>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Kitchen new-order alert sound</h3>
              <div>
                <div className="md:col-span-2">
                <label className="settings-switch cursor-pointer">
                  <span className="text-sm font-medium text-slate-800">Enable repeating sound until order is started</span>
                  <input
                    type="checkbox"
                    checked={currentSettings.kitchenSLA?.alertSoundEnabled !== false}
                    onChange={(e) =>
                      setCurrentSettings((prev) => ({
                        ...prev,
                        kitchenSLA: {
                          ...prev.kitchenSLA,
                          alertSoundEnabled: e.target.checked,
                        },
                      }))
                    }
                    className="h-4 w-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                  />
                </label>
                </div>
                <div className="md:col-span-2 settings-field">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Volume ({currentSettings.kitchenSLA?.alertSoundVolume ?? 100}%)
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={currentSettings.kitchenSLA?.alertSoundVolume ?? 100}
                    onChange={(e) =>
                      setCurrentSettings((prev) => ({
                        ...prev,
                        kitchenSLA: {
                          ...prev.kitchenSLA,
                          alertSoundVolume: Math.min(100, Math.max(0, Number(e.target.value))),
                        },
                      }))
                    }
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-pink-600"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Kitchen Display uses this level for the new-order alert. Use Test sound to hear it.
                  </p>
                </div>
                <div className="md:col-span-2 settings-field">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sound URL</label>
                  <input
                    type="url"
                    value={currentSettings.kitchenSLA?.alertSoundUrl || ''}
                    onChange={(e) =>
                      setCurrentSettings((prev) => ({
                        ...prev,
                        kitchenSLA: {
                          ...prev.kitchenSLA,
                          alertSoundUrl: e.target.value,
                        },
                      }))
                    }
                    placeholder="/sounds/kitchen.mp3 or https://..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                  />
                </div>
                <div className="md:col-span-2 settings-field settings-span">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Upload custom sound file</label>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="file"
                      accept="audio/*"
                      disabled={isUploadingAlertSound}
                      onChange={(e) => {
                        const file = e.target.files?.[0] ?? null;
                        void handleUploadKitchenAlertSound(file);
                        e.currentTarget.value = '';
                      }}
                      className="block text-sm text-gray-700 file:mr-3 file:px-3 file:py-2 file:rounded-md file:border-0 file:bg-pink-50 file:text-pink-700 hover:file:bg-pink-100"
                    />
                    <button
                      type="button"
                      onClick={handleTestKitchenAlertSound}
                      className="px-3 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Test sound
                    </button>
                    {isUploadingAlertSound && <span className="text-sm text-gray-500">Uploading...</span>}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Upload audio (mp3/wav/etc) up to 10MB. After upload, click Save Settings.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className={`${isSection('food-cost') ? '' : 'hidden '}bg-white shadow-sm border border-slate-200 rounded-xl p-6 space-y-4`}>
            <h2 className="text-2xl font-semibold text-gray-900 mb-1">Food cost &amp; recipes</h2>
            <p className="text-sm text-gray-500 mb-4">
              Choose how recipes and food cost work: link recipes to inventory (stock depletes on sale, cost from inventory) or define cost per unit on each recipe (no inventory, no depletion).
            </p>
            <div className="settings-field settings-span">
              <label className="block text-sm font-medium text-gray-700 mb-2">Recipe / inventory mode</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="recipeInventoryMode"
                    checked={(currentSettings.recipeInventoryMode ?? 'inventory_connected') === 'inventory_connected'}
                    onChange={() => setCurrentSettings((prev) => ({ ...prev, recipeInventoryMode: 'inventory_connected' }))}
                    className="text-pink-500 focus:ring-pink-500"
                  />
                  <span>Use inventory connection — recipes link to inventory items; stock depletes on payment; food cost from inventory cost per unit.</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="recipeInventoryMode"
                    checked={(currentSettings.recipeInventoryMode ?? 'inventory_connected') === 'cost_only'}
                    onChange={() => setCurrentSettings((prev) => ({ ...prev, recipeInventoryMode: 'cost_only' }))}
                    className="text-pink-500 focus:ring-pink-500"
                  />
                  <span>Use recipe cost only — define product name, quantity, unit and cost per unit on each recipe; no inventory depletion; food cost from recipe-defined costs.</span>
                </label>
              </div>
            </div>
          </div>

          {currentUser?.isAdmin && (
          <div className={`${isSection('flash-pl') ? '' : 'hidden '}bg-white shadow-sm border border-slate-200 rounded-xl p-6 space-y-4`}>
            <h2 className="text-2xl font-semibold text-gray-900 mb-1">Flash P&L (Investor Format)</h2>
            <p className="text-sm text-gray-500 mb-4">
              Optional values for the Flash P&L report in the Executive Dashboard. Leave blank to show placeholders.
            </p>
            <div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">Weekly revenue budget ($)</label>
                <input type="number" min="0" step="0.01" value={currentSettings.flashPnl?.weeklyRevenueBudget ?? ''} onChange={(e) => setCurrentSettings((prev) => ({ ...prev, flashPnl: { ...prev.flashPnl, weeklyRevenueBudget: e.target.value === '' ? undefined : Number(e.target.value) } }))} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" placeholder="Budget" />
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">COGS target (%)</label>
                <input type="number" min="0" max="100" step="0.1" value={currentSettings.flashPnl?.cogsTargetPercent ?? ''} onChange={(e) => setCurrentSettings((prev) => ({ ...prev, flashPnl: { ...prev.flashPnl, cogsTargetPercent: e.target.value === '' ? undefined : Number(e.target.value) } }))} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" placeholder="e.g. 30" />
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">Labor target (%)</label>
                <input type="number" min="0" max="100" step="0.1" value={currentSettings.flashPnl?.laborTargetPercent ?? ''} onChange={(e) => setCurrentSettings((prev) => ({ ...prev, flashPnl: { ...prev.flashPnl, laborTargetPercent: e.target.value === '' ? undefined : Number(e.target.value) } }))} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" placeholder="e.g. 25" />
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">Labor cost ($)</label>
                <input type="number" min="0" step="0.01" value={currentSettings.flashPnl?.laborCost ?? ''} onChange={(e) => setCurrentSettings((prev) => ({ ...prev, flashPnl: { ...prev.flashPnl, laborCost: e.target.value === '' ? undefined : Number(e.target.value) } }))} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" />
                <p className="text-xs text-gray-500 mt-0.5">Manual total; or use Labor rate below for timesheet-derived cost.</p>
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">Labor rate ($/hr)</label>
                <input type="number" min="0" step="0.01" value={currentSettings.flashPnl?.laborRatePerHour ?? ''} onChange={(e) => setCurrentSettings((prev) => ({ ...prev, flashPnl: { ...prev.flashPnl, laborRatePerHour: e.target.value === '' ? undefined : Number(e.target.value) } }))} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" placeholder="e.g. 25" />
                <p className="text-xs text-gray-500 mt-0.5">When set, Flash P&amp;L and Labor report use timesheet hours × this rate.</p>
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">Overtime cost ($)</label>
                <input type="number" min="0" step="0.01" value={currentSettings.flashPnl?.overtimeCost ?? ''} onChange={(e) => setCurrentSettings((prev) => ({ ...prev, flashPnl: { ...prev.flashPnl, overtimeCost: e.target.value === '' ? undefined : Number(e.target.value) } }))} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" />
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">Rent ($)</label>
                <input type="number" min="0" step="0.01" value={currentSettings.flashPnl?.fixedCostsRent ?? ''} onChange={(e) => setCurrentSettings((prev) => ({ ...prev, flashPnl: { ...prev.flashPnl, fixedCostsRent: e.target.value === '' ? undefined : Number(e.target.value) } }))} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" />
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">Insurance ($)</label>
                <input type="number" min="0" step="0.01" value={currentSettings.flashPnl?.fixedCostsInsurance ?? ''} onChange={(e) => setCurrentSettings((prev) => ({ ...prev, flashPnl: { ...prev.flashPnl, fixedCostsInsurance: e.target.value === '' ? undefined : Number(e.target.value) } }))} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" />
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">Salaries ($)</label>
                <input type="number" min="0" step="0.01" value={currentSettings.flashPnl?.fixedCostsSalaries ?? ''} onChange={(e) => setCurrentSettings((prev) => ({ ...prev, flashPnl: { ...prev.flashPnl, fixedCostsSalaries: e.target.value === '' ? undefined : Number(e.target.value) } }))} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" />
              </div>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-1">Cash position / Bank balance ($)</label>
                <input type="number" step="0.01" value={currentSettings.flashPnl?.cashPosition ?? ''} onChange={(e) => setCurrentSettings((prev) => ({ ...prev, flashPnl: { ...prev.flashPnl, cashPosition: e.target.value === '' ? undefined : Number(e.target.value) } }))} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" />
              </div>
            </div>
          </div>
          )}

          {/* Override Reasons Configuration */}
          <div className={`${isSection('overrides') ? '' : 'hidden '}bg-white shadow-sm border border-slate-200 rounded-xl p-6 space-y-4`}>
            <h2 className="text-2xl font-semibold text-gray-900 mb-1">Manager Override Reasons</h2>
            <p className="text-sm text-gray-500 mb-4">
              Configure the list of reasons available when managers override orders (comp without payment).
            </p>
            <OverrideReasonsConfig
              reasons={currentSettings.overrideReasons || []}
              onUpdate={(reasons) =>
                setCurrentSettings((prev) => ({
                  ...prev,
                  overrideReasons: reasons,
                }))
              }
            />
          </div>

          {/* Fire Mode Configuration */}
          <div className={`${isSection('fire') ? '' : 'hidden '}bg-white shadow-sm border border-slate-200 rounded-xl p-6 space-y-4`}>
            <h2 className="text-2xl font-semibold text-gray-900 mb-1">Fire Evacuation Mode Settings</h2>
            <p className="text-sm text-gray-500 mb-4">
              Configure how the system handles orders during fire evacuation or fire drill situations.
            </p>
            <div className="space-y-4">
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Order Action During Fire Mode
                </label>
                <select
                  value={currentSettings.fireMode?.orderAction || 'pause'}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      fireMode: {
                        ...prev.fireMode,
                        orderAction: e.target.value as 'pause' | 'cancel' | 'hold',
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                >
                  <option value="pause">Pause (resume after evacuation)</option>
                  <option value="cancel">Cancel with notification</option>
                  <option value="hold">Hold for later processing</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {currentSettings.fireMode?.orderAction === 'pause' && 'Orders will be paused and can be resumed after fire mode is deactivated.'}
                  {currentSettings.fireMode?.orderAction === 'cancel' && 'Active orders will be cancelled and customers will be notified.'}
                  {currentSettings.fireMode?.orderAction === 'hold' && 'Orders will be held and processed after fire mode is deactivated.'}
                </p>
              </div>
              <div className="settings-field settings-span">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Message (Optional)
                </label>
                <textarea
                  value={currentSettings.fireMode?.notificationMessage || ''}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      fireMode: {
                        ...prev.fireMode,
                        notificationMessage: e.target.value,
                      },
                    }))
                  }
                  placeholder="Enter notification message template for customers..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">
                  This message will be sent to customers if order action is set to "Cancel".
                </p>
              </div>
            </div>
          </div>

          {/* Buffet packages */}
          <div className={`${isSection('buffet') ? '' : 'hidden '}bg-white shadow-sm border border-slate-200 rounded-xl p-6 space-y-2`}>
            <h2 className="text-2xl font-semibold text-gray-900 mb-1">Buffet packages</h2>
            <p className="text-sm text-gray-500">
              Configure packages under <span className="font-semibold text-gray-700">Menu → Buffet packages</span>
              (not as menu items). Staff pick them on POS after choosing Buffet → Breakfast / Lunch / Dinner.
            </p>
          </div>

          {/* First Table Discount Configuration */}
          <div className={`${isSection('first-table') ? '' : 'hidden '}bg-white shadow-sm border border-slate-200 rounded-xl p-6 space-y-4`}>
            <h2 className="text-2xl font-semibold text-gray-900 mb-1">First Table Discount</h2>
            <p className="text-sm text-gray-500 mb-4">
              Configure a First Table discount that applies only to selected menu categories. Staff can choose this discount method in POS,
              and the percentage will be applied to eligible items on the bill.
            </p>

            <div className="space-y-4">
              <label className="settings-switch cursor-pointer">
                <input
                  type="checkbox"
                  checked={currentSettings.firstTable?.enabled ?? false}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      firstTable: {
                        ...(prev.firstTable || {}),
                        enabled: e.target.checked,
                        discountPercent: prev.firstTable?.discountPercent ?? 50,
                        eligibleCategoryIds: prev.firstTable?.eligibleCategoryIds ?? [],
                        eligibleMenuItemIds: prev.firstTable?.eligibleMenuItemIds ?? [],
                      },
                    }))
                  }
                  className="rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                />
                <span className="text-sm font-medium text-gray-700">Enable First Table discount</span>
              </label>

              <div>
                <div className="settings-field">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount Percentage (% off)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={currentSettings.firstTable?.discountPercent ?? 50}
                    onChange={(e) => {
                      const value = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                      setCurrentSettings((prev) => ({
                        ...prev,
                        firstTable: {
                          ...(prev.firstTable || {}),
                          enabled: prev.firstTable?.enabled ?? true,
                          discountPercent: value,
                          eligibleCategoryIds: prev.firstTable?.eligibleCategoryIds ?? [],
                          eligibleMenuItemIds: prev.firstTable?.eligibleMenuItemIds ?? [],
                        },
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    placeholder="50"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    For First Table bookings, this percentage will be taken off eligible food items (e.g. 50% off).
                  </p>
                </div>

                <div className="settings-field settings-span">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Eligible Categories
                  </label>
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1">
                    {categories.length === 0 ? (
                      <p className="text-xs text-gray-500 italic">
                        No categories found. Configure categories in Menu Management first.
                      </p>
                    ) : (
                      categories
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((category) => {
                          const id = category.id;
                          const currentIds = currentSettings.firstTable?.eligibleCategoryIds ?? [];
                          const checked = currentIds.some((cid) => String(cid) === String(id));
                          return (
                            <label
                              key={String(id)}
                              className="flex items-center gap-2 text-xs sm:text-sm text-gray-700"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  setCurrentSettings((prev) => {
                                    const prevIds = prev.firstTable?.eligibleCategoryIds ?? [];
                                    const idStr = String(id);
                                    const nextIds = e.target.checked
                                      ? [...prevIds.filter((cid) => String(cid) !== idStr), id]
                                      : prevIds.filter((cid) => String(cid) !== idStr);
                                    return {
                                      ...prev,
                                      firstTable: {
                                        ...(prev.firstTable || {}),
                                        enabled: prev.firstTable?.enabled ?? true,
                                        discountPercent: prev.firstTable?.discountPercent ?? 50,
                                        eligibleCategoryIds: nextIds,
                                      },
                                    };
                                  });
                                }}
                                className="rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                              />
                              <span className="truncate">{category.name}</span>
                            </label>
                          );
                        })
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Items in these categories will receive the First Table discount (in addition to any specific items selected below). Other items remain at full price.
                  </p>
                </div>
              </div>

              <div className="settings-field settings-span">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Eligible Items (optional)
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Select individual items to be eligible even if their category is not selected above.
                </p>
                <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto p-2 space-y-1">
                  {categories.map((cat) => {
                    const catItems = menuItems.filter((m) => String(m.categoryId) === String(cat.id));
                    if (catItems.length === 0) return null;
                    return (
                      <div key={String(cat.id)} className="space-y-1">
                        <div className="text-xs font-semibold text-gray-500 sticky top-0 bg-gray-50 py-1 px-1">
                          {cat.name}
                        </div>
                        {catItems
                          .slice()
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((item: MenuItem) => {
                            const id = item.id as number;
                            const checked = (currentSettings.firstTable?.eligibleMenuItemIds ?? []).includes(id);
                            return (
                              <label
                                key={item.id}
                                className="flex items-center gap-2 py-1 px-2 hover:bg-gray-50 rounded cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    const ids = currentSettings.firstTable?.eligibleMenuItemIds ?? [];
                                    const next = e.target.checked ? [...ids, id] : ids.filter((i) => i !== id);
                                    setCurrentSettings((prev) => ({
                                      ...prev,
                                      firstTable: {
                                        ...(prev.firstTable || {}),
                                        enabled: prev.firstTable?.enabled ?? true,
                                        discountPercent: prev.firstTable?.discountPercent ?? 50,
                                        eligibleCategoryIds: prev.firstTable?.eligibleCategoryIds ?? [],
                                        eligibleMenuItemIds: next,
                                      },
                                    }));
                                  }}
                                  className="rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                                />
                                <span className="text-sm truncate">{item.name}</span>
                                <span className="text-xs text-gray-400">${item.price.toFixed(2)}</span>
                              </label>
                            );
                          })}
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Discount applies if an item is in a selected category <span className="font-medium">or</span> is selected here.
                </p>
              </div>
            </div>
          </div>

          {/* Event / Stall mode (Mobile POS) */}
          <div className={`${isSection('event') ? '' : 'hidden '}bg-white shadow-sm border border-slate-200 rounded-xl p-6 space-y-4`}>
            <h2 className="text-2xl font-semibold text-gray-900 mb-1">Event / Stall (Mobile POS)</h2>
            <p className="text-sm text-gray-500 mb-4">
              Use Mobile POS for events or stalls outside the restaurant. Event mode uses a fully separate menu with independent pricing.
            </p>
            <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-4 space-y-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Event profiles</h3>
                <p className="text-xs text-gray-600 mt-1">
                  Create new events, archive past events, and switch which event is currently active in Mobile POS.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 items-end">
                <label className="flex flex-col text-xs font-medium text-gray-700 min-w-[220px] flex-1">
                  Active event
                  <select
                    className="mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-normal"
                    value={currentSettings.eventStall?.activeEventId ?? ''}
                    onChange={(e) => switchActiveEvent(e.target.value)}
                  >
                    {(currentSettings.eventStall?.events ?? []).map((evt) => (
                      <option key={evt.id} value={evt.id}>
                        {evt.name} {evt.status === 'archived' ? '(archived)' : '(active)'}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  disabled={!activeEventProfile || activeEventProfile.status === 'archived'}
                  onClick={archiveActiveEvent}
                  className="px-3 py-2 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-sm font-semibold hover:bg-amber-100 disabled:opacity-50"
                >
                  Archive selected
                </button>
              </div>
              <div className="flex flex-wrap gap-2 items-end">
                <label className="flex flex-col text-xs font-medium text-gray-700 min-w-[220px] flex-1">
                  New event name
                  <input
                    type="text"
                    value={newEventName}
                    onChange={(e) => setNewEventName(e.target.value)}
                    className="mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-normal"
                    placeholder="e.g. Winter Night Market 2026"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => createEventProfile(true)}
                  className="px-3 py-2 bg-pink-500 text-white text-sm font-semibold rounded-md hover:bg-pink-600"
                >
                  Create from current menu
                </button>
                <button
                  type="button"
                  onClick={() => createEventProfile(false)}
                  className="px-3 py-2 bg-gray-200 text-gray-800 text-sm font-semibold rounded-md hover:bg-gray-300"
                >
                  Create blank event
                </button>
              </div>
            </div>
            <div className="space-y-4">
              <label className="settings-switch cursor-pointer">
                <input
                  type="checkbox"
                  checked={currentSettings.eventStall?.enabled ?? false}
                  onChange={(e) =>
                    setCurrentSettings((prev) => ({
                      ...prev,
                      eventStall: {
                        ...prev.eventStall,
                        enabled: e.target.checked,
                        name: prev.eventStall?.name ?? '',
                        eventMenuItems: prev.eventStall?.eventMenuItems ?? [],
                        menuItemIds: prev.eventStall?.menuItemIds ?? [],
                      },
                    }))
                  }
                  className="rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                />
                <span className="text-sm font-medium text-gray-700">Enable Event / Stall mode</span>
              </label>
              <div className="settings-field">
                <label className="block text-sm font-medium text-gray-700 mb-2">Event name (optional)</label>
                <input
                  type="text"
                  value={activeEventProfile?.name ?? currentSettings.eventStall?.name ?? ''}
                  onChange={(e) => {
                    const nextName = e.target.value;
                    syncActiveEventInState((active) => ({ ...active, name: nextName }));
                  }}
                  className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                  placeholder="e.g. Christmas Market"
                />
              </div>
              <div className="settings-field settings-span">
                <label className="block text-sm font-medium text-gray-700 mb-2">Event card terminals</label>
                <p className="text-xs text-gray-500 mb-2">
                  Select one or more Smartpay terminals used only for Event / Stall card payments on Mobile POS. These terminals are hidden from the main restaurant POS. If none are selected, Event card payments are disabled until you assign at least one terminal here.
                </p>
                <ul className="max-w-md space-y-2 border border-gray-200 rounded-md p-3 bg-white">
                  {(currentSettings.smartpay?.terminals ?? []).length === 0 ? (
                    <li className="text-sm text-gray-500">Add Smartpay terminals under Integrations first.</li>
                  ) : (
                    (currentSettings.smartpay?.terminals ?? []).map((t) => {
                      const selected = collectEventTerminalIdsFromStall(currentSettings.eventStall).includes(t.id);
                      return (
                        <li key={t.id} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`event-terminal-${t.id}`}
                            checked={selected}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setCurrentSettings((prev) => {
                                const cur = collectEventTerminalIdsFromStall(prev.eventStall);
                                const nextSet = new Set(cur);
                                if (checked) nextSet.add(t.id);
                                else nextSet.delete(t.id);
                                const ids = [...nextSet];
                                return {
                                  ...prev,
                                  eventStall: {
                                    ...prev.eventStall,
                                    eventTerminalIds: ids,
                                    ...(ids.length ? { eventTerminalId: ids[0] } : { eventTerminalId: undefined }),
                                  },
                                };
                              });
                            }}
                            className="rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                          />
                          <label htmlFor={`event-terminal-${t.id}`} className="text-sm text-gray-800 cursor-pointer flex-1">
                            {t.name}
                            <span className="text-gray-500 ml-1">{t.isPaired ? '(paired)' : '(not paired)'}</span>
                          </label>
                        </li>
                      );
                    })
                  )}
                </ul>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Event operators (Mobile POS PIN)</h3>
                  <p className="text-xs text-gray-600 mt-1">
                    Optional named operators unlock Event Mobile POS with a PIN (stored as hash + salt; not reversible). Save Settings to persist. Changing PIN regenerates salt/hash.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 items-end">
                  <label className="flex flex-col text-xs font-medium text-gray-700 min-w-[140px] flex-1">
                    Name
                    <input
                      type="text"
                      value={newEventOperatorName}
                      onChange={(e) => setNewEventOperatorName(e.target.value)}
                      className="mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-normal"
                      placeholder="e.g. Sam"
                      autoComplete="off"
                    />
                  </label>
                  <label className="flex flex-col text-xs font-medium text-gray-700 min-w-[120px] flex-1">
                    PIN
                    <input
                      type="password"
                      value={newEventOperatorPin}
                      onChange={(e) => setNewEventOperatorPin(e.target.value)}
                      className="mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-normal"
                      placeholder="4+ characters"
                      autoComplete="new-password"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => void addEventOperatorRow()}
                    className="px-3 py-2 bg-pink-500 text-white text-sm font-semibold rounded-md hover:bg-pink-600"
                  >
                    Add operator
                  </button>
                </div>
                <ul className="space-y-2">
                  {(currentSettings.eventStall?.eventOperators ?? []).length === 0 ? (
                    <li className="text-sm text-gray-500 italic">No operators — Mobile POS Event mode is open without PIN.</li>
                  ) : (
                    (currentSettings.eventStall?.eventOperators ?? []).map((op) => (
                      <li key={op.id} className="flex flex-wrap items-center gap-2 justify-between bg-white border border-gray-200 rounded-md px-3 py-2">
                        <span className="text-sm font-medium text-gray-900">{op.name}</span>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => void resetEventOperatorPin(op)}
                            className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                          >
                            Reset PIN
                          </button>
                          <button
                            type="button"
                            onClick={() => removeEventOperatorRow(op.id)}
                            className="text-xs px-2 py-1 rounded bg-red-50 border border-red-200 text-red-700 hover:bg-red-100"
                          >
                            Remove
                          </button>
                        </div>
                      </li>
                    ))
                  )}
                </ul>
              </div>
              <div className={activeEventArchived ? 'opacity-70' : undefined}>
                <label className="block text-sm font-medium text-gray-700 mb-2">Event menu items</label>
                <p className="text-xs text-gray-500 mb-2">
                  Add standalone Event items here. Each item must have its own Event price and category.
                </p>
                {activeEventArchived ? (
                  <p className="text-xs text-amber-700 mb-2">
                    Archived events are read-only. Switch to an active event to edit menu items.
                  </p>
                ) : null}
                <div className="border border-gray-200 rounded-lg p-3 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={eventMenuDraft.name}
                      onChange={(e) => setEventMenuDraft((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Item name"
                      className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    />
                    <input
                      type="text"
                      value={eventMenuDraft.categoryName}
                      onChange={(e) => setEventMenuDraft((prev) => ({ ...prev, categoryName: e.target.value }))}
                      placeholder="Category"
                      className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    />
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={eventMenuDraft.price}
                      onChange={(e) => setEventMenuDraft((prev) => ({ ...prev, price: e.target.value }))}
                      placeholder="Price"
                      className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={eventMenuDraft.unitCost}
                      onChange={(e) => setEventMenuDraft((prev) => ({ ...prev, unitCost: e.target.value }))}
                      placeholder="Unit cost (optional, ex GST)"
                      className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    />
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={eventMenuDraft.openingQuantity}
                      onChange={(e) => setEventMenuDraft((prev) => ({ ...prev, openingQuantity: e.target.value }))}
                      placeholder="Opening qty (optional)"
                      className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    />
                    <input
                      type="url"
                      value={eventMenuDraft.imageUrl}
                      onChange={(e) => setEventMenuDraft((prev) => ({ ...prev, imageUrl: e.target.value }))}
                      placeholder="Image URL (optional)"
                      className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={eventMenuDraft.isAvailable}
                      onChange={(e) => setEventMenuDraft((prev) => ({ ...prev, isAvailable: e.target.checked }))}
                      className="rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                    />
                    Available in Event mode
                  </label>
                  {eventMenuError && (
                    <p className="text-sm text-red-600">{eventMenuError}</p>
                  )}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={upsertEventMenuItem}
                      className="px-3 py-2 bg-pink-500 text-white font-semibold rounded-md hover:bg-pink-600"
                    >
                      {editingEventMenuItemId == null ? 'Add Event item' : 'Update Event item'}
                    </button>
                    {editingEventMenuItemId != null && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditingEventMenuItemId(null);
                          setEventMenuDraft(emptyEventMenuDraft);
                          setEventMenuError(null);
                        }}
                        className="px-3 py-2 bg-gray-200 text-gray-700 font-semibold rounded-md hover:bg-gray-300"
                      >
                        Cancel edit
                      </button>
                    )}
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto p-2 space-y-3">
                  {(() => {
                    const items = [...(currentSettings.eventStall?.eventMenuItems ?? [])].sort(
                      (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || a.name.localeCompare(b.name)
                    );
                    if (items.length === 0) {
                      return (
                        <p className="text-sm text-gray-500 italic px-1 py-2">
                          No Event menu items configured yet.
                        </p>
                      );
                    }
                    const grouped = items.reduce<Record<string, EventStallMenuItem[]>>((acc, item) => {
                      const key = item.categoryName;
                      acc[key] = acc[key] || [];
                      acc[key].push(item);
                      return acc;
                    }, {});
                    const catOrder = currentSettings.eventStall?.eventCategoryOrder ?? [];
                    const categoryKeys = mergeEventCategoryOrder(Object.keys(grouped), catOrder);
                    return categoryKeys.map((categoryName) => {
                      const catItems = grouped[categoryName];
                      if (!catItems) return null;
                      return (
                        <div key={categoryName} className="space-y-1">
                          <div className="text-xs font-semibold text-gray-500 sticky top-0 bg-gray-50 py-1 px-1">{categoryName}</div>
                          {catItems.map((item) => (
                            <div key={item.id} className="flex items-center gap-2 py-1 px-2 rounded bg-gray-50">
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
                                <p className="text-xs text-gray-500">
                                  ${item.price.toFixed(2)}
                                  {item.unitCost != null && Number.isFinite(item.unitCost)
                                    ? ` · cost $${item.unitCost.toFixed(2)}`
                                    : ''}
                                  {item.isAvailable === false ? ' · hidden' : ''}
                                  {item.soldOut ? ' · sold out' : ''}
                                </p>
                                <p className="text-xs text-indigo-700">
                                  {item.openingQuantity != null
                                    ? `Balance: ${Math.max(0, item.openingQuantity - (eventSoldQuantityByItemId.get(item.id) ?? 0))} / ${item.openingQuantity}`
                                    : 'Balance: Unlimited'}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => replenishEventMenuItem(item)}
                                className="text-xs px-2 py-1 rounded bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                              >
                                Replenish
                              </button>
                              <button
                                type="button"
                                onClick={() => toggleEventMenuSoldOut(item)}
                                className={`text-xs px-2 py-1 rounded border ${
                                  item.soldOut
                                    ? 'bg-amber-100 border-amber-300 text-amber-900'
                                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                {item.soldOut ? 'Sold out' : 'Mark sold out'}
                              </button>
                              <button
                                type="button"
                                onClick={() => startEditEventMenuItem(item)}
                                className="text-xs px-2 py-1 rounded bg-white border border-gray-300 text-gray-700 hover:bg-gray-100"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => removeEventMenuItem(item.id)}
                                className="text-xs px-2 py-1 rounded bg-red-50 border border-red-200 text-red-700 hover:bg-red-100"
                              >
                                Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      );
                    });
                  })()}
                </div>
                {(() => {
                  const items = currentSettings.eventStall?.eventMenuItems ?? [];
                  const names = [...new Set(items.map((i) => String(i.categoryName ?? '').trim()).filter(Boolean))];
                  const ordered = mergeEventCategoryOrder(names, currentSettings.eventStall?.eventCategoryOrder ?? []);
                  if (names.length <= 1) return null;
                  return (
                    <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 mt-3 space-y-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Category order (Mobile POS)</p>
                        <p className="text-xs text-gray-600 mt-0.5">
                          Controls tab order and grouped sections on the Event order screen. Use ↑ ↓ to reorder.
                        </p>
                      </div>
                      <ul className="space-y-1">
                        {ordered.map((cat) => (
                          <li key={cat} className="flex items-center gap-2 rounded-md bg-white border border-gray-200 px-2 py-1.5 text-sm">
                            <span className="flex-1 min-w-0 truncate font-medium text-gray-800">{cat}</span>
                            <button
                              type="button"
                              disabled={ordered.indexOf(cat) === 0}
                              onClick={() => moveEventCategoryOrder(cat, -1)}
                              className="px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 text-xs"
                              aria-label={`Move ${cat} up`}
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              disabled={ordered.indexOf(cat) >= ordered.length - 1}
                              onClick={() => moveEventCategoryOrder(cat, 1)}
                              className="px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 text-xs"
                              aria-label={`Move ${cat} down`}
                            >
                              ↓
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })()}
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800">Token counter reset</p>
                      <p className="text-xs text-gray-600 mt-1">
                        {currentSettings.eventStall?.tokenCounterResetAt
                          ? `Last reset: ${new Date(currentSettings.eventStall.tokenCounterResetAt).toLocaleString()}`
                          : 'Not set (tokens start from the beginning).'}
                      </p>
                      <p className="text-[11px] text-gray-500 mt-1">
                        Next new order gets ticket #1 after reset. Use this when ticket numbering should restart from the beginning.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const ok = window.confirm('Reset the Event token counter? This affects displayed token numbers for orders after the reset time.');
                        if (!ok) return;
                        setCurrentSettings((prev) => ({
                          ...prev,
                          eventStall: {
                            ...prev.eventStall,
                            tokenCounterResetAt: Date.now(),
                          },
                        }));
                      }}
                      className="px-3 py-2 bg-amber-500 text-white font-semibold rounded-md hover:bg-amber-600"
                    >
                      Reset token counter
                    </button>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                    <label className="block text-sm font-medium text-gray-800" htmlFor="settings-event-token-pad">
                      Physical pad size (optional)
                    </label>
                    <p className="text-xs text-gray-600">
                      Queue slip numbers wrap from this value back to 1 (e.g. 100 for pads numbered 1–100). Leave blank for unlimited sequential ticket numbers.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        id="settings-event-token-pad"
                        type="number"
                        inputMode="numeric"
                        min={2}
                        max={9999}
                        placeholder="e.g. 100"
                        value={eventTokenPadDraft}
                        onChange={(e) => setEventTokenPadDraft(e.target.value)}
                        onBlur={() => {
                          const t = eventTokenPadDraft.trim();
                          if (t !== '' && !Number.isFinite(Number(t))) {
                            const r = currentSettings.eventStall?.eventTokenRollMax;
                            setEventTokenPadDraft(r != null && r >= 2 ? String(r) : '');
                            return;
                          }
                          setCurrentSettings((prev) => ({
                            ...prev,
                            eventStall: {
                              ...prev.eventStall,
                              eventTokenRollMax:
                                t === '' ? 0 : Math.min(9999, Math.max(2, Math.floor(Number(t)))),
                            },
                          }));
                        }}
                        className="w-32 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                      />
                      <span className="text-xs text-gray-500">Saved when you click Save settings below.</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Uber/DoorDash manual orders – price increase */}
          <div className={`${isSection('delivery-manual') ? '' : 'hidden '}bg-white shadow-sm border border-slate-200 rounded-xl p-6 space-y-4`}>
            <h2 className="text-2xl font-semibold text-gray-900 mb-1">Uber/DoorDash manual orders</h2>
            <p className="text-sm text-gray-500 mb-4">
              When entering Uber Eats or DoorDash orders manually in the POS, you can add a percentage to the total so the order total matches the platform sales price (e.g. 15 for 15%).
            </p>
            <div className="settings-field">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price increase (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={currentSettings.uberDoordashPriceIncreasePercent ?? 0}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  const num = isNaN(value) ? 0 : Math.max(0, Math.min(100, value));
                  setCurrentSettings((prev) => ({
                    ...prev,
                    uberDoordashPriceIncreasePercent: num,
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                placeholder="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Set to 0 to use menu prices as-is. Example: 15 adds 15% to the order total.
              </p>
            </div>
          </div>

           <div className={isSection('platforms') ? undefined : 'hidden'}>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Delivery Platforms</h2>
              <div className="space-y-6">
                <DeliveryPlatformSettings 
                  platform="Uber Eats" 
                  config={currentSettings['Uber Eats']} 
                  onChange={handleDeliveryChange}
                  currentUser={currentUser}
                  onUpdateApproval={handleDeliveryApproval}
                />
                <DeliveryPlatformSettings 
                  platform="DoorDash" 
                  config={currentSettings['DoorDash']} 
                  onChange={handleDeliveryChange}
                  currentUser={currentUser}
                  onUpdateApproval={handleDeliveryApproval}
                />
              </div>
          </div>
        </div>
      </main>
      </div>
    </div>
    {isPrinterModalOpen && <PrinterModal printer={editingPrinter} onClose={() => setIsPrinterModalOpen(false)} onSave={onSavePrinter} />}
    </>
  );
};

export default SettingsPage;